import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle, Calendar, Instagram, CheckCircle2, Settings, Trash2, Loader2, ShoppingBag, Package, RefreshCw, ExternalLink, FileText, Lock, AlertTriangle, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { MessengerIcon } from "@/components/icons/MessengerIcon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { EvolutionApiDialog } from "@/components/integrations/EvolutionApiDialog";
import { WhatsAppBusinessConnectDialog } from "@/components/integrations/WhatsAppBusinessConnectDialog";
import { GoogleCalendarDialog } from "@/components/integrations/GoogleCalendarDialog";
import { CalendarEventConfigDialog } from "@/components/integrations/CalendarEventConfigDialog";
import { ShopifyDialog } from "@/components/integrations/ShopifyDialog";
import { NetfaceDialog } from "@/components/integrations/NetfaceDialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { PaywallOverlay } from "@/components/layout/PaywallOverlay";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { useUserRole } from "@/hooks/useUserRole";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";
import { OFFLINE_CONNECTION_STATES } from "@/lib/uazapi-connection-state";
import { isUazapiEnabledForWorkspace } from "@/lib/uazapi-feature-flags";
import { SHOPIFY_UI_ENABLED } from "@/lib/shopify-ui-flag";
import type { GoogleCalendarConnection } from "@/hooks/useGoogleCalendars";
import {
  DisconnectIntegrationDialog,
  type DisconnectIntegrationTarget,
  type ReassignIntegrationOption,
} from "@/components/integrations/DisconnectIntegrationDialog";
import {
  applyDisconnectConversationChoice,
  type DisconnectConversationChoice,
} from "@/lib/conversation-integration-assign";
import { fetchIntegrationConversationCount } from "@/lib/inbox-stats";
import {
  integrationDisconnectDetail,
  integrationDisconnectLabel,
} from "@/lib/integration-disconnect-labels";
import { whatsappIntegrationDisplayLabel } from "@/lib/whatsapp-integration-label";
import { WhatsAppCloudWabaStatusCard } from "@/components/integrations/WhatsAppCloudWabaStatusCard";
import { resolveFunnelSelectLabel } from "@/lib/funnel-display-label";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";
import { IntegrationInboundLeadFields } from "@/components/integrations/IntegrationInboundLeadFields";

type IntegrationAgentOption = { id: string; name: string; is_active: boolean };

function formatIntegrationAgentLabel(agent: IntegrationAgentOption): string {
  return agent.is_active ? agent.name : `${agent.name} (Inativo)`;
}

function IntegrationAgentSelectItems({ agents }: { agents: IntegrationAgentOption[] }) {
  return (
    <>
      <SelectItem value="none">Nenhum agente</SelectItem>
      {agents.map((agent) => (
        <SelectItem key={agent.id} value={agent.id}>
          {formatIntegrationAgentLabel(agent)}
        </SelectItem>
      ))}
    </>
  );
}

const INTEGRATION_AGENT_HINT =
  "O agente pré-selecionado só atenderá quando estiver ativo na página de Agentes.";

const WHATSAPP_BUSINESS_ACCOUNT_RECOMMENDATION = (
  <Alert variant="default" className="border-amber-500/40 bg-amber-500/5">
    <AlertTriangle className="h-4 w-4 text-amber-600" />
    <AlertTitle className="text-sm text-amber-800 dark:text-amber-200">
      Recomendação importante: WhatsApp Business
    </AlertTitle>
    <AlertDescription className="text-xs text-muted-foreground">
      É altamente recomendado usar contas do WhatsApp Business em vez do WhatsApp normal para integração.
      O WhatsApp normal pode apresentar inconsistências, desconexões, limitações e instabilidades durante o uso com a nossa API.
    </AlertDescription>
  </Alert>
);

const WHATSAPP_CLOUD_COEXISTENCE_HINT = (
  <Alert variant="default" className="border-muted">
    <AlertDescription className="text-xs text-muted-foreground">
      <span className="font-medium text-foreground/80">Coexistência:</span> mantenha o app WhatsApp Business instalado e aberto durante o onboarding.
      A Meta sincroniza contatos e histórico em até 24h.
    </AlertDescription>
  </Alert>
);

const PLAN_LABELS: Record<string, string> = {
  inicial: "Essencial",
  intermediario: "Plus",
  pro: "PRO",
  enterprise: "Enterprise",
};

// Workspaces com acesso antecipado ao Messenger (enquanto a integração está em beta fechado).
const MESSENGER_ALLOWED_WORKSPACES = new Set<string>([
  "9c074afa-6c08-4ef7-ba5a-a4551a6eac0f", // atendimento.fabtech@gmail.com
]);

const WHATSAPP_CLOUD_ALLOWED_WORKSPACES = new Set<string>([
  "9c074afa-6c08-4ef7-ba5a-a4551a6eac0f", // atendimento.fabtech@gmail.com
  "c1a642ec-ed71-4218-a058-cc4db8e989a9", // salivarematriz@gmail.com
]);

const integrations = [
  {
    name: "WhatsApp Business",
    description: "Conecte seu número do WhatsApp para conversas automatizadas",
    status: "active",
    Icon: MessageCircle,
    type: "evolution_api",
  },
  {
    name: "Instagram",
    description: "Automatize respostas em DMs e comentários",
    status: "active",
    Icon: Instagram,
    type: "instagram",
  },
  {
    name: "Messenger",
    description: "Facebook Messenger com IA — disponível em breve",
    status: "active",
    Icon: MessengerIcon,
    type: "messenger",
  },
  {
    name: "Google Calendar",
    description: "Agendamentos automáticos de reuniões via IA",
    status: "active",
    Icon: Calendar,
    type: "google_calendar",
  },
  {
    name: "Shopify",
    description: "Importe e sincronize produtos da sua loja Shopify",
    status: "active",
    Icon: ShoppingBag,
    type: "shopify",
  },
  {
    name: "Netface",
    description: "Sincronize os seus produtos da Netface",
    status: "active",
    Icon: Package,
    type: "netface",
  },
];

const WHATSAPP_CLOUD_INTEGRATION = {
  name: "WhatsApp API Oficial",
  description: "Integração oficial do WhatsApp Business",
  status: "active" as const,
  Icon: MessageCircle,
  type: "whatsapp_cloud",
};

const getWhatsAppCloudAccountUrl = (config: Record<string, unknown> | null | undefined): string | null => {
  const wabaId = typeof config?.waba_id === "string" ? config.waba_id : null;
  const businessId = typeof config?.business_id === "string" ? config.business_id : null;
  if (wabaId && businessId) {
    return `https://business.facebook.com/latest/whatsapp_manager/phone_numbers/?business_id=${businessId}&asset_id=${wabaId}`;
  }
  if (wabaId) {
    return `https://business.facebook.com/wa/manage/home/?waba_id=${wabaId}`;
  }
  return null;
};

// Função para formatar número de telefone brasileiro
const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  // Formato: +55 (11) 99999-9999 (13 dígitos)
  if (cleaned.length === 13) {
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 9)}-${cleaned.slice(9)}`;
  }
  // Formato: +55 (11) 9999-9999 (12 dígitos)
  if (cleaned.length === 12) {
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
  }
  // Formato: (11) 99999-9999 (11 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Formato: (11) 9999-9999 (10 dígitos)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const getConnectionState = (integration: any): string =>
  String(integration?.config?.connection_state || "").toLowerCase();

const isConnectionStateOffline = (integration: any): boolean =>
  OFFLINE_CONNECTION_STATES.has(getConnectionState(integration));

const needsWhatsappReconnect = (integration: any): boolean =>
  integration?.status === "disconnected" ||
  integration?.status === "error" ||
  isConnectionStateOffline(integration);

type AccordionIntegrationItem =
  | (typeof integrations)[number]
  | typeof WHATSAPP_CLOUD_INTEGRATION;

type AccordionConnectionContext = {
  workspaceId: string | null | undefined;
  isOwner: boolean;
  uazapiAllowed: boolean;
  whatsappIntegrations: any[];
  whatsappUazapiIntegrations: any[];
  whatsappCloudIntegrations: any[];
  activeIntegrations: Record<string, any>;
  calendarConnections: GoogleCalendarConnection[];
  calendarTokenInvalid: boolean;
};

function isAccordionIntegrationConnected(
  integration: AccordionIntegrationItem,
  ctx: AccordionConnectionContext,
): boolean {
  const messengerAllowed = MESSENGER_ALLOWED_WORKSPACES.has(ctx.workspaceId ?? "");
  const isComingSoon =
    (!ctx.isOwner && (integration.type === "shopify" || integration.type === "netface")) ||
    (integration.type === "messenger" && !messengerAllowed);

  if (isComingSoon) return false;

  if (integration.type === "evolution_api") {
    const displayedWhatsAppConnections = ctx.uazapiAllowed
      ? (ctx.whatsappUazapiIntegrations.length > 0
          ? ctx.whatsappUazapiIntegrations
          : ctx.whatsappIntegrations)
      : ctx.whatsappIntegrations;
    return displayedWhatsAppConnections.some(
      (w) => w.status === "active" && !needsWhatsappReconnect(w),
    );
  }

  if (integration.type === "whatsapp_cloud") {
    return ctx.whatsappCloudIntegrations.some((w) => w.status === "active");
  }

  if (integration.type === "google_calendar") {
    return isGcalMultiCalendarEnabled()
      ? ctx.calendarConnections.some(
          (c) => !c.needs_reconnect && c.syncCalendars.some((sc) => sc.is_enabled),
        ) || (ctx.calendarConnections.length > 0 && !ctx.calendarTokenInvalid)
      : Boolean(ctx.activeIntegrations.google_calendar && !ctx.calendarTokenInvalid);
  }

  if (integration.type === "instagram") {
    const data = ctx.activeIntegrations.instagram;
    const isDisconnected = data && (data.status === "disconnected" || data.status === "error");
    const isTokenExpired =
      data?.status === "active" &&
      data?.token_expires_at &&
      new Date(data.token_expires_at) < new Date();
    return data?.status === "active" && !isTokenExpired && !isDisconnected;
  }

  if (integration.type === "messenger") {
    const data = ctx.activeIntegrations.messenger;
    const isDisconnected = data && (data.status === "disconnected" || data.status === "error");
    const isTokenExpired =
      data?.status === "active" &&
      data?.token_expires_at &&
      new Date(data.token_expires_at) < new Date();
    return data?.status === "active" && !isTokenExpired && !isDisconnected;
  }

  if (integration.type === "shopify") {
    return ctx.activeIntegrations.shopify?.status === "active";
  }

  if (integration.type === "netface") {
    const data = ctx.activeIntegrations.netface;
    return data?.status === "active" || data?.status === "pending";
  }

  return false;
}

function sortAccordionIntegrations(
  items: AccordionIntegrationItem[],
  ctx: AccordionConnectionContext,
): AccordionIntegrationItem[] {
  return [...items]
    .map((item, index) => ({
      item,
      index,
      connected: isAccordionIntegrationConnected(item, ctx),
    }))
    .sort((a, b) => {
      if (a.connected !== b.connected) return a.connected ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ item }) => item);
}

// Feature flag — manter o botão de sincronização de histórico oculto até nova ordem.
const SHOW_WHATSAPP_HISTORY_SYNC = false;

export default function Integrations() {
  const [evolutionDialogOpen, setEvolutionDialogOpen] = useState(false);
  const [evolutionReconnectMode, setEvolutionReconnectMode] = useState(false);
  const [evolutionReconnectIntegration, setEvolutionReconnectIntegration] = useState<any>(null);
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false);
  const [businessReconnectMode, setBusinessReconnectMode] = useState(false);
  const [businessReconnectIntegration, setBusinessReconnectIntegration] = useState<any>(null);
  const [businessMigrateFromIntegrationId, setBusinessMigrateFromIntegrationId] = useState<string | null>(null);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarDialogMode, setCalendarDialogMode] = useState<"manage" | "pick-one">("pick-one");
  const [calendarDialogCredentialId, setCalendarDialogCredentialId] = useState<string | undefined>();
  const [calendarConfigOpen, setCalendarConfigOpen] = useState(false);
  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);
  const [netfaceDialogOpen, setNetfaceDialogOpen] = useState(false);
  const [instagramConnecting, setInstagramConnecting] = useState(false);
  const [activeIntegrations, setActiveIntegrations] = useState<Record<string, any>>({});
  const [whatsappIntegrations, setWhatsappIntegrations] = useState<any[]>([]);
  const [whatsappUazapiIntegrations, setWhatsappUazapiIntegrations] = useState<any[]>([]);
  const [whatsappCloudList, setWhatsappCloudList] = useState<any[]>([]);
  const [calendarName, setCalendarName] = useState<string | null>(null);
  const [calendarConnections, setCalendarConnections] = useState<GoogleCalendarConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [calendarTokenInvalid, setCalendarTokenInvalid] = useState(false);
  const [calendarReconnecting, setCalendarReconnecting] = useState(false);
  const [agents, setAgents] = useState<IntegrationAgentOption[]>([]);
  const [updatingAgent, setUpdatingAgent] = useState<string | null>(null);
  const [funnels, setFunnels] = useState<Array<{ id: string; name: string; is_default: boolean | null }>>([]);
  const [updatingFunnelForIntegrationId, setUpdatingFunnelForIntegrationId] = useState<string | null>(null);
  const [updatingCommentDmForIntegrationId, setUpdatingCommentDmForIntegrationId] = useState<string | null>(null);
  const [instagramFunnelDialogOpen, setInstagramFunnelDialogOpen] = useState(false);
  const [instagramFunnelMode, setInstagramFunnelMode] = useState<"auto" | "existing">("auto");
  const [instagramSelectedFunnelId, setInstagramSelectedFunnelId] = useState<string>("");
  const [instagramMediaCount, setInstagramMediaCount] = useState<number | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<DisconnectIntegrationTarget | null>(null);
  const [disconnectConversationCount, setDisconnectConversationCount] = useState(0);
  const [disconnectReassignOptions, setDisconnectReassignOptions] = useState<ReassignIntegrationOption[]>([]);
  const [disconnectPreparing, setDisconnectPreparing] = useState(false);
  const [syncingIntegrationId, setSyncingIntegrationId] = useState<string | null>(null);
  const [integrationUpgradeOpen, setIntegrationUpgradeOpen] = useState(false);
  const [integrationLimitInfo, setIntegrationLimitInfo] = useState<{
    current: number;
    limit: number;
    plan: string;
  } | null>(null);

  const handleSyncWhatsappHistory = async (integrationId: string) => {
    setSyncingIntegrationId(integrationId);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-sync-history", {
        body: { integration_id: integrationId },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast({
        title: "Sincronização iniciada",
        description:
          "Estamos importando o histórico em segundo plano. O agente NÃO vai responder a essas mensagens — apenas as novas a partir de agora.",
      });
    } catch (e: any) {
      let description = e?.message || "Erro ao iniciar a sincronização.";
      if (e?.context && typeof e.context.clone === "function") {
        try {
          const payload = await e.context.clone().json();
          description = payload?.error || payload?.message || description;
        } catch {
          // Mantém a mensagem padrão quando a resposta não vier em JSON.
        }
      }
      toast({
        title: "Não foi possível sincronizar",
        description,
        variant: "destructive",
      });
    } finally {
      // Libera o botão; o status real é refletido via realtime no config da integração.
      setTimeout(() => setSyncingIntegrationId(null), 1500);
    }
  };
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);
  const { toast } = useToast();
  const { shouldShowPaywall } = useSubscription();
  const { checkCanCreate, checkCanConnectIntegration } = useResourceLimits(workspaceId);
  const { isOwner } = useUserRole();
  const navigate = useNavigate();

  const showIntegrationUpgradeDialog = (check: { current: number; limit: number; plan: string }) => {
    setIntegrationLimitInfo(check);
    setIntegrationUpgradeOpen(true);
  };

  const ensureCanConnectMessagingChannel = async (): Promise<boolean> => {
    const check = await checkCanConnectIntegration();
    if (!check.can_create) {
      showIntegrationUpgradeDialog(check);
      return false;
    }
    return true;
  };

  const isReconnectSameActiveSlot = (type: string): boolean => {
    if (type === "instagram") {
      return activeIntegrations.instagram?.status === "active";
    }
    if (type === "messenger") {
      return activeIntegrations.messenger?.status === "active";
    }
    return false;
  };

  useEffect(() => {
    if (workspaceId) {
      loadIntegrations();
      loadAgents();
      loadFunnels();
    }
  }, [workspaceId]);

  // Handle Shopify OAuth result returned via URL query (fallback when the
  // popup couldn't postMessage back to the opener — e.g. blocked by the
  // browser, or the user opened in the same tab).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const status = params.get("shopify");
    if (!status) return;

    const cleanShopifyQuery = () => {
      params.delete("shopify");
      params.delete("code");
      params.delete("message");
      const next = params.toString();
      const cleanUrl = `${window.location.pathname}${next ? `?${next}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", cleanUrl);
    };

    if (!SHOPIFY_UI_ENABLED) {
      cleanShopifyQuery();
      return;
    }

    const FRIENDLY: Record<string, string> = {
      invalid_hmac: "A autorização do Shopify não foi reconhecida. Tente novamente.",
      invalid_state: "A autorização demorou demais e expirou. Inicie a conexão novamente.",
      shop_mismatch: "A loja autorizada não é a mesma que você informou. Confira o domínio e tente de novo.",
      token_exchange_failed: "O Shopify recusou a autorização. Verifique se você concluiu o consentimento.",
      persist_failed: "Não conseguimos salvar a conexão agora. Tente novamente em instantes.",
      not_configured: "A integração Shopify ainda não está configurada nesta conta.",
      missing_params: "A resposta do Shopify veio incompleta. Tente conectar novamente.",
      invalid_shop_domain: "O domínio recebido do Shopify é inválido. Tente conectar novamente.",
      unknown: "Algo deu errado ao concluir a conexão. Tente novamente.",
    };

    if (status === "connected") {
      toast({
        title: "Shopify conectado!",
        description: "Estamos sincronizando seus produtos. Isso pode levar alguns minutos.",
      });
      if (workspaceId) loadIntegrations();
    } else if (status === "error") {
      const code = params.get("code") || "unknown";
      toast({
        title: "Erro ao conectar Shopify",
        description: FRIENDLY[code] || FRIENDLY.unknown,
        variant: "destructive",
      });
    }

    cleanShopifyQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Realtime: keep WhatsApp/integration cards in sync without manual refresh.
  // When the Evolution webhook saves a new QR or promotes status to 'active'
  // (with phone_number), the matching card updates live — no F5 needed.
  useEffect(() => {
    if (!workspaceId) return;
    const channel = supabase
      .channel(`integrations-${workspaceId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'integrations',
          filter: `workspace_id=eq.${workspaceId}`,
        },
        (payload) => {
          const newRow = payload.new as any;
          if (!newRow?.id) return;

          if (newRow.provider === 'whatsapp_evolution') {
            setWhatsappIntegrations((prev) => {
              const idx = prev.findIndex((i) => i.id === newRow.id);
              if (idx === -1) {
                if (['active', 'pending', 'disconnected', 'error'].includes(newRow.status)) {
                  return [...prev, newRow];
                }
                return prev;
              }
              const next = [...prev];
              const prevRow = next[idx];
              const merged = { ...prevRow, ...newRow };
              // Realtime pode mandar payload parcial; não sobrescrever agentes com undefined.
              if (!Object.prototype.hasOwnProperty.call(newRow, "assigned_agent_id")) {
                merged.assigned_agent_id = prevRow.assigned_agent_id;
              }
              if (!Object.prototype.hasOwnProperty.call(newRow, "comment_agent_id")) {
                merged.comment_agent_id = prevRow.comment_agent_id;
              }
              next[idx] = merged;
              return next;
            });
          } else if (newRow.provider === 'whatsapp_uazapi') {
            setWhatsappUazapiIntegrations((prev) => {
              const idx = prev.findIndex((i) => i.id === newRow.id);
              if (idx === -1) {
                if (['active', 'pending', 'disconnected', 'error'].includes(newRow.status)) {
                  return [...prev, newRow];
                }
                return prev;
              }
              const next = [...prev];
              const prevRow = next[idx];
              const merged = { ...prevRow, ...newRow };
              if (!Object.prototype.hasOwnProperty.call(newRow, "assigned_agent_id")) {
                merged.assigned_agent_id = prevRow.assigned_agent_id;
              }
              if (!Object.prototype.hasOwnProperty.call(newRow, "comment_agent_id")) {
                merged.comment_agent_id = prevRow.comment_agent_id;
              }
              next[idx] = merged;
              return next;
            });
          } else if (newRow.provider === 'whatsapp_cloud') {
            setWhatsappCloudList((prev) => {
              const idx = prev.findIndex((i) => i.id === newRow.id);
              if (idx === -1) {
                if (['active', 'pending', 'disconnected', 'error'].includes(newRow.status)) {
                  return [...prev, newRow];
                }
                return prev;
              }
              const next = [...prev];
              const prevRow = next[idx];
              const merged = { ...prevRow, ...newRow };
              if (!Object.prototype.hasOwnProperty.call(newRow, "assigned_agent_id")) {
                merged.assigned_agent_id = prevRow.assigned_agent_id;
              }
              if (!Object.prototype.hasOwnProperty.call(newRow, "config")) {
                merged.config = prevRow.config;
              }
              next[idx] = merged;
              return next;
            });
          } else {
            setActiveIntegrations((prev) => {
              const existing = prev[newRow.provider];
              if (!existing || existing.id === newRow.id) {
                const merged = { ...(existing || {}), ...newRow };
                if (!Object.prototype.hasOwnProperty.call(newRow, "assigned_agent_id")) {
                  merged.assigned_agent_id = existing?.assigned_agent_id;
                }
                if (!Object.prototype.hasOwnProperty.call(newRow, "comment_agent_id")) {
                  merged.comment_agent_id = existing?.comment_agent_id;
                }
                return { ...prev, [newRow.provider]: merged };
              }
              return prev;
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [workspaceId]);

  // Fetch IG media_count whenever an active Instagram integration is loaded.
  // Used to display a diagnostic badge when the connected account has no posts
  // (Meta won't deliver `comments` webhooks for accounts with media_count=0).
  useEffect(() => {
    const igActive = activeIntegrations.instagram?.status === "active";
    if (!workspaceId || !igActive) {
      setInstagramMediaCount(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("instagram-media-count", {
          body: { workspace_id: workspaceId },
        });
        if (cancelled) return;
        if (error) {
          setInstagramMediaCount(null);
          return;
        }
        setInstagramMediaCount(typeof data?.media_count === "number" ? data.media_count : null);
      } catch {
        if (!cancelled) setInstagramMediaCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, activeIntegrations.instagram?.id, activeIntegrations.instagram?.status]);



  const loadAgents = async () => {
    if (!workspaceId) return;
    
    const { data } = await supabase
      .from("agents")
      .select("id, name, is_active")
      .eq("workspace_id", workspaceId)
      .order("is_active", { ascending: false })
      .order("name");
    
    if (data) {
      setAgents(data);
    }
  };

  const loadFunnels = async () => {
    if (!workspaceId) return;
    const { data, error } = await supabase
      .from("sales_funnels")
      .select("id, name, is_default, integration_id")
      .eq("workspace_id", workspaceId)
      .order("is_default", { ascending: false })
      .order("order_index", { ascending: true });
    if (error) {
      console.error("Erro ao carregar funis:", error);
      return;
    }
    setFunnels(data || []);
  };

  const handleUpdateIntegrationAgent = async (
    integrationId: string, 
    field: "assigned_agent_id" | "comment_agent_id", 
    agentId: string | null
  ) => {
    setUpdatingAgent(`${integrationId}-${field}`);
    try {
      const { error } = await supabase.rpc("update_integration_agent", {
        p_integration_id: integrationId,
        p_field: field,
        p_agent_id: agentId,
      });

      if (error) throw error;

      // Atualização otimista por linha — mantém cada cartão isolado até o reload confirmar.
      setWhatsappIntegrations((prev) => {
        if (!prev.some((i) => i.id === integrationId)) return prev;
        return prev.map((i) =>
          i.id === integrationId ? { ...i, [field]: agentId } : i
        );
      });
      setWhatsappCloudList((prev) => {
        if (!prev.some((i) => i.id === integrationId)) return prev;
        return prev.map((i) =>
          i.id === integrationId ? { ...i, [field]: agentId } : i
        );
      });
      setActiveIntegrations((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const mapKey of Object.keys(next)) {
          const row = next[mapKey] as { id?: string } | undefined;
          if (row && typeof row === "object" && row.id === integrationId) {
            next[mapKey] = { ...row, [field]: agentId };
            changed = true;
            break;
          }
        }
        return changed ? next : prev;
      });
      
      toast({
        title: "Agente atualizado",
        description: field === "comment_agent_id" 
          ? "Agente de comentários atualizado com sucesso."
          : "Agente de DM atualizado com sucesso.",
      });
      
      loadIntegrations();
    } catch (error) {
      console.error("Error updating agent:", error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o agente",
        variant: "destructive",
      });
    } finally {
      setUpdatingAgent(null);
    }
  };

  const handleUpdateIntegrationFunnel = async (integrationId: string, funnelId: string | null) => {
    setUpdatingFunnelForIntegrationId(integrationId);
    try {
      const { data: integration, error: integrationError } = await supabase
        .from("integrations")
        .select("id, workspace_id, config")
        .eq("id", integrationId)
        .single();
      if (integrationError || !integration) throw integrationError || new Error("Integração não encontrada");

      if (funnelId) {
        const { data: funnel } = await supabase
          .from("sales_funnels")
          .select("id")
          .eq("id", funnelId)
          .eq("workspace_id", integration.workspace_id)
          .maybeSingle();
        if (!funnel) throw new Error("Funil inválido para este workspace");
      }

      const currentConfig = (integration.config as Record<string, unknown> | null) || {};
      const previousFunnelId =
        typeof currentConfig.selected_funnel_id === "string"
          ? currentConfig.selected_funnel_id
          : null;
      const clearStage = !funnelId || funnelId !== previousFunnelId;
      const nextConfig = {
        ...currentConfig,
        selected_funnel_id: funnelId,
        ...(clearStage ? { selected_stage_slug: null } : {}),
      };

      const { error } = await supabase
        .from("integrations")
        .update({ config: nextConfig })
        .eq("id", integrationId);
      if (error) throw error;

      setWhatsappIntegrations((prev) =>
        prev.map((i) =>
          i.id === integrationId ? { ...i, config: { ...(i.config || {}), ...nextConfig } } : i
        )
      );
      setWhatsappCloudList((prev) =>
        prev.map((i) =>
          i.id === integrationId ? { ...i, config: { ...(i.config || {}), ...nextConfig } } : i
        )
      );
      setWhatsappUazapiIntegrations((prev) =>
        prev.map((i) =>
          i.id === integrationId ? { ...i, config: { ...(i.config || {}), ...nextConfig } } : i
        )
      );
      setActiveIntegrations((prev) => {
        let changed = false;
        const next = { ...prev };
        for (const mapKey of Object.keys(next)) {
          const row = next[mapKey] as { id?: string; config?: Record<string, unknown> } | undefined;
          if (row && typeof row === "object" && row.id === integrationId) {
            next[mapKey] = { ...row, config: { ...(row.config || {}), ...nextConfig } };
            changed = true;
            break;
          }
        }
        return changed ? next : prev;
      });

      toast({
        title: "Funil atualizado",
        description: funnelId
          ? "Novos leads dessa conexão entrarão no funil selecionado."
          : "Modo automático ativado para essa conexão.",
      });
      loadIntegrations();
    } catch (error: any) {
      console.error("Erro ao atualizar funil da integração:", error);
      toast({
        title: "Erro ao atualizar funil",
        description: error?.message || "Não foi possível salvar a configuração.",
        variant: "destructive",
      });
    } finally {
      setUpdatingFunnelForIntegrationId(null);
    }
  };

  const applyIntegrationConfigLocally = (
    integrationId: string,
    nextConfig: Record<string, unknown>,
  ) => {
    const patchRow = (row: { id: string; config?: Record<string, unknown> }) =>
      row.id === integrationId
        ? { ...row, config: { ...(row.config || {}), ...nextConfig } }
        : row;
    setWhatsappIntegrations((prev) => prev.map(patchRow));
    setWhatsappCloudList((prev) => prev.map(patchRow));
    setWhatsappUazapiIntegrations((prev) => prev.map(patchRow));
    setActiveIntegrations((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const mapKey of Object.keys(next)) {
        const row = next[mapKey] as { id?: string; config?: Record<string, unknown> } | undefined;
        if (row && typeof row === "object" && row.id === integrationId) {
          next[mapKey] = patchRow(row as { id: string; config?: Record<string, unknown> });
          changed = true;
          break;
        }
      }
      return changed ? next : prev;
    });
  };

  const handleUpdateIntegrationCommentDm = async (
    integrationId: string,
    patch: { comment_dm_enabled?: boolean; comment_dm_mode?: string },
  ) => {
    setUpdatingCommentDmForIntegrationId(integrationId);
    try {
      const { data: integration, error: integrationError } = await supabase
        .from("integrations")
        .select("id, workspace_id, config")
        .eq("id", integrationId)
        .single();
      if (integrationError || !integration) {
        throw integrationError || new Error("Integração não encontrada");
      }

      const currentConfig = (integration.config as Record<string, unknown> | null) || {};
      const nextConfig = { ...currentConfig, ...patch };

      const { error } = await supabase
        .from("integrations")
        .update({ config: nextConfig })
        .eq("id", integrationId);
      if (error) throw error;

      toast({
        title: "Configuração de DM atualizada",
        description: patch.comment_dm_enabled
          ? "Private Reply habilitado para comentários desta conta."
          : "Private Reply desabilitado para esta conta.",
      });
      loadIntegrations();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Não foi possível salvar.";
      toast({
        title: "Erro ao atualizar DM",
        description: message,
        variant: "destructive",
      });
    } finally {
      setUpdatingCommentDmForIntegrationId(null);
    }
  };

  // Derive calendar display name from persisted data without calling Google API
  const getCalendarDisplayName = (calendarData: any): string => {
    if (calendarData.calendar_display_name) return calendarData.calendar_display_name;
    if (!calendarData.calendar_id || calendarData.calendar_id === 'primary') return 'Agenda Principal';
    return 'Agenda Conectada';
  };

  const openCalendarManageDialog = (credentialId?: string) => {
    setCalendarDialogMode(isGcalMultiCalendarEnabled() ? "manage" : "pick-one");
    setCalendarDialogCredentialId(credentialId);
    setCalendarDialogOpen(true);
  };

  const handleDisconnectCalendar = async (credentialId: string) => {
    if (!workspaceId) return;
    try {
      try {
        await supabase.functions.invoke("google-calendar-sync", {
          body: { action: "stop_watch", workspaceId, reason: "disconnect" },
        });
      } catch (watchError) {
        console.warn("Failed to stop Google Calendar watch:", watchError);
      }

      const { error } = await supabase.functions.invoke("google-calendar-tools", {
        body: {
          action: "disconnect",
          workspaceId,
          params: { credentialId },
        },
      });

      if (error) throw error;

      toast({ title: "Google Calendar desconectado" });
      loadIntegrations();
    } catch (error) {
      console.error("Error disconnecting calendar:", error);
      toast({
        title: "Erro ao desconectar",
        description: "Não foi possível desconectar o Google Calendar",
        variant: "destructive",
      });
    }
  };

  const handleAddCalendarAgenda = async () => {
    const check = await checkCanCreate("calendars");
    if (!check.can_create) {
      toast({
        title: "Limite de agendas atingido",
        description: `Seu plano (${check.plan}) permite até ${check.limit} agenda(s). Você já possui ${check.current}.`,
        variant: "destructive",
      });
      return;
    }
    openCalendarManageDialog(calendarConnections[0]?.id);
  };

  // Conexões WhatsApp que ficaram marcadas como "conectando"/pendentes são
  // reconciliadas com o servidor uma única vez por carregamento da página.
  const syncStuckUazapiConnections = async (rows: any[]) => {
    if (!workspaceId) return;
    const stuck = rows.filter((r) => r.status !== "active" || isConnectionStateOffline(r));
    if (stuck.length === 0) return;

    let changed = false;
    for (const row of stuck.slice(0, 5)) {
      try {
        const { data } = await supabase.functions.invoke("uazapi-connect", {
          body: { action: "sync_status", workspaceId, integrationId: row.id },
        });
        if (data?.connectionState === "connected") changed = true;
      } catch {
        // Silencioso: é uma reconciliação de fundo.
      }
    }
    if (changed) loadIntegrations();
  };

  const loadIntegrations = async () => {
    if (!workspaceId) return;

    setLoading(true);
    setCalendarTokenInvalid(false);
    try {
      // Load regular integrations — explicit columns, NEVER select shopify_access_token
      const { data: rawData, error } = await supabase
        .from("integrations")
        .select("id, workspace_id, provider, status, config, instance_identifier, instagram_account_id, assigned_agent_id, comment_agent_id, shop_domain, shopify_api_version, token_expires_at, created_at, updated_at")
        .eq("workspace_id", workspaceId)
        .in("status", ["active", "pending", "disconnected", "error"]);
      const data = rawData as any[] | null;

      if (error) throw error;

      const integrationsMap: Record<string, any> = {};
      const waIntegrations: any[] = [];
      const waUazapiIntegrations: any[] = [];
      const waCloudIntegrations: any[] = [];
      data?.forEach((integration) => {
        if (integration.provider === 'whatsapp_evolution') {
          waIntegrations.push(integration);
        } else if (integration.provider === 'whatsapp_uazapi') {
          waUazapiIntegrations.push(integration);
        } else if (integration.provider === 'whatsapp_cloud') {
          waCloudIntegrations.push(integration);
        } else {
          const existing = integrationsMap[integration.provider];
          if (!existing || 
              (integration.status === 'active') ||
              (integration.status === 'pending' && existing.status === 'disconnected')) {
            integrationsMap[integration.provider] = integration;
          }
        }
      });
      setWhatsappCloudList(waCloudIntegrations);

      // Load Google Calendar connections — explicit select, no tokens
      const { data: calendarRows } = await supabase
        .from("google_calendar_credentials")
        .select("id, email, is_active, calendar_id, needs_reconnect, calendar_display_name")
        .eq("workspace_id", workspaceId)
        .eq("is_active", true)
        .order("created_at", { ascending: true });

      let syncCalendars: Array<{
        id: string;
        credential_id: string;
        calendar_id: string;
        display_name: string | null;
        is_enabled: boolean;
        is_primary: boolean;
      }> = [];

      if (isGcalMultiCalendarEnabled() && calendarRows?.length) {
        const { data: syncData } = await supabase
          .from("google_calendar_sync_calendars")
          .select("id, credential_id, calendar_id, display_name, is_enabled, is_primary")
          .eq("workspace_id", workspaceId)
          .order("is_primary", { ascending: false })
          .order("created_at", { ascending: true });
        syncCalendars = syncData ?? [];
      }

      const connections: GoogleCalendarConnection[] = (calendarRows ?? []).map((cred) => ({
        ...cred,
        needs_reconnect: cred.needs_reconnect === true,
        syncCalendars: syncCalendars.filter((sc) => sc.credential_id === cred.id),
      }));

      setCalendarConnections(connections);

      if (connections.length > 0) {
        const primary = connections[0];
        integrationsMap.google_calendar = primary;
        if (primary.needs_reconnect) {
          setCalendarTokenInvalid(true);
        } else {
          setCalendarTokenInvalid(false);
        }
        if (isGcalMultiCalendarEnabled()) {
          const enabled = primary.syncCalendars.filter((sc) => sc.is_enabled);
          setCalendarName(
            enabled.length > 0
              ? enabled.map((sc) => sc.display_name || sc.calendar_id).join(", ")
              : getCalendarDisplayName(primary),
          );
        } else {
          setCalendarName(getCalendarDisplayName(primary));
        }
      } else {
        setCalendarName(null);
      }

      setWhatsappIntegrations(waIntegrations);
      setWhatsappUazapiIntegrations(waUazapiIntegrations);
      setActiveIntegrations(integrationsMap);

      // Reconcilia conexões WhatsApp presas em "conectando": o pareamento pode
      // ter terminado depois que o diálogo do QR foi fechado, sem evento de webhook.
      void syncStuckUazapiConnections(waUazapiIntegrations);

    } catch (error) {
      console.error("Erro ao carregar integrações:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReconnectCalendar = async () => {
    if (!workspaceId) return;

    setCalendarReconnecting(true);
    try {
      // Iniciar processo de reconexão — o callback OAuth reseta needs_reconnect
      const { data, error } = await supabase.functions.invoke(
        `google-calendar-oauth?workspace_id=${workspaceId}`,
        { method: 'GET' }
      );

      if (error) {
        toast({
          title: "Erro ao reconectar",
          description: "Não foi possível iniciar a reconexão",
          variant: "destructive",
        });
        return;
      }

      if (data?.authUrl) {
        window.open(data.authUrl, "_blank", "width=600,height=700");
        toast({
          title: "Autorização necessária",
          description: "Complete a autorização na janela do Google",
        });

        // Recarregar integrações após um delay para verificar conexão
        setTimeout(() => {
          loadIntegrations();
        }, 3000);
      }
    } catch (error) {
      console.error("Error reconnecting calendar:", error);
      toast({
        title: "Erro ao reconectar",
        description: "Não foi possível reconectar o Google Calendar",
        variant: "destructive",
      });
    } finally {
      setCalendarReconnecting(false);
    }
  };

  const handleConfigure = async (type: string) => {
    // Bloquear apenas Shopify para não-owners
    if ((type === "shopify" || type === "netface") && !isOwner) {
      toast({
        title: "Funcionalidade em breve",
        description: "Esta integração estará disponível em breve. Fique atento às atualizações!",
      });
      return;
    }

    if (type === "evolution_api") {
      if (!(await ensureCanConnectMessagingChannel())) return;
      if (uazapiAllowed) {
        setBusinessReconnectMode(false);
        setBusinessReconnectIntegration(null);
        setBusinessMigrateFromIntegrationId(null);
        setBusinessDialogOpen(true);
      } else {
        setEvolutionReconnectMode(false);
        setEvolutionReconnectIntegration(null);
        setEvolutionDialogOpen(true);
      }
    } else if (type === "google_calendar") {
      if (isGcalMultiCalendarEnabled()) {
        if (calendarConnections.length > 0) {
          const check = await checkCanCreate("calendars");
          if (!check.can_create) {
            toast({
              title: "Limite de agendas atingido",
              description: `Seu plano (${check.plan}) permite até ${check.limit} agenda(s). Você já possui ${check.current}. Faça upgrade para adicionar mais.`,
              variant: "destructive",
            });
            return;
          }
          setCalendarDialogCredentialId(calendarConnections[0]?.id);
        } else {
          setCalendarDialogCredentialId(undefined);
        }
        setCalendarDialogMode("manage");
      } else {
        setCalendarDialogMode("pick-one");
        setCalendarDialogCredentialId(undefined);
      }
      setCalendarDialogOpen(true);
    } else if (type === "instagram") {
      if (!isReconnectSameActiveSlot("instagram") && !(await ensureCanConnectMessagingChannel())) return;
      setInstagramFunnelMode("auto");
      setInstagramSelectedFunnelId("");
      setInstagramFunnelDialogOpen(true);
    } else if (type === "messenger") {
      if (!MESSENGER_ALLOWED_WORKSPACES.has(workspaceId ?? "") && !activeIntegrations.messenger) {
        toast({
          title: "Em breve",
          description: "A integração com o Messenger estará disponível em breve.",
        });
        return;
      }
      if (!isReconnectSameActiveSlot("messenger") && !(await ensureCanConnectMessagingChannel())) return;
      await handleMessengerConnect();
    } else if (type === "shopify") {
      setShopifyDialogOpen(true);
    } else if (type === "netface") {
      setNetfaceDialogOpen(true);
    }
  };

  const handleInstagramConnect = async () => {
    if (!workspaceId) return;

    setInstagramConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("instagram-oauth", {
        body: {
          workspace_id: workspaceId,
          selected_funnel_id: instagramFunnelMode === "existing" ? instagramSelectedFunnelId : null,
        }
      });

      if (error) throw error;

      if (data?.authUrl) {
        window.open(data.authUrl, '_blank', 'width=600,height=700');
        
        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'instagram-connected') {
            toast({
              title: "Instagram conectado!",
              description: "Sua conta do Instagram foi conectada com sucesso.",
            });
            loadIntegrations();
            window.removeEventListener('message', handleMessage);
          } else if (event.data.type === 'instagram-error') {
            toast({
              title: "Erro ao conectar Instagram",
              description: event.data.error || "Não foi possível conectar a conta do Instagram.",
              variant: "destructive",
            });
            window.removeEventListener('message', handleMessage);
          }
        };
        
        window.addEventListener('message', handleMessage);
      }
    } catch (error) {
      console.error("Error connecting Instagram:", error);
      toast({
        title: "Erro ao conectar",
        description: "Não foi possível iniciar a conexão com Instagram",
        variant: "destructive",
      });
    } finally {
      setInstagramConnecting(false);
    }
  };

  const handleMessengerConnect = async () => {
    if (!workspaceId) return;

    setInstagramConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("messenger-oauth", {
        body: { workspace_id: workspaceId }
      });

      if (error) throw error;

      if (data?.authUrl) {
        window.open(data.authUrl, '_blank', 'width=600,height=700');
        
        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'messenger-connected') {
            toast({
              title: "Messenger conectado!",
              description: "Sua página do Facebook Messenger foi conectada com sucesso.",
            });
            loadIntegrations();
            window.removeEventListener('message', handleMessage);
          }
        };
        
        window.addEventListener('message', handleMessage);
      }
    } catch (error) {
      console.error("Error connecting Messenger:", error);
      toast({
        title: "Erro ao conectar",
        description: "Não foi possível iniciar a conexão com Messenger",
        variant: "destructive",
      });
    } finally {
      setInstagramConnecting(false);
    }
  };

  const handleDialogClose = (open: boolean) => {
    setEvolutionDialogOpen(open);
    if (!open) {
      setEvolutionReconnectMode(false);
      setEvolutionReconnectIntegration(null);
      loadIntegrations();
    }
  };

  const handleBusinessDialogClose = (open: boolean) => {
    setBusinessDialogOpen(open);
    if (!open) {
      setBusinessReconnectMode(false);
      setBusinessReconnectIntegration(null);
      setBusinessMigrateFromIntegrationId(null);
      loadIntegrations();
    }
  };

  const handleReconnectWhatsApp = (integrationData: any) => {
    if (uazapiAllowed) {
      if (integrationData.provider === "whatsapp_evolution") {
        setBusinessMigrateFromIntegrationId(integrationData.id);
        setBusinessReconnectIntegration(integrationData);
        setBusinessReconnectMode(true);
        setBusinessDialogOpen(true);
      } else {
        handleReconnectWhatsAppBusiness(integrationData);
      }
      return;
    }
    setEvolutionReconnectIntegration(integrationData);
    setEvolutionReconnectMode(true);
    setEvolutionDialogOpen(true);
  };

  const handleReconnectWhatsAppBusiness = (integrationData: any) => {
    setBusinessMigrateFromIntegrationId(null);
    setBusinessReconnectIntegration(integrationData);
    setBusinessReconnectMode(true);
    setBusinessDialogOpen(true);
  };

  const openWhatsAppBusinessConnect = async () => {
    if (!(await ensureCanConnectMessagingChannel())) return;
    setBusinessReconnectMode(false);
    setBusinessReconnectIntegration(null);
    setBusinessMigrateFromIntegrationId(null);
    setBusinessDialogOpen(true);
  };

  const openWhatsAppLegacyConnect = async () => {
    if (!(await ensureCanConnectMessagingChannel())) return;
    setEvolutionReconnectMode(false);
    setEvolutionReconnectIntegration(null);
    setEvolutionDialogOpen(true);
  };

  const MESSAGING_PROVIDERS = ["whatsapp_evolution", "whatsapp_uazapi", "whatsapp_cloud", "instagram", "messenger"] as const;

  const openDisconnectDialog = async (integrationId: string) => {
    if (!workspaceId) return;

    setDisconnectPreparing(true);
    try {
      const { data: integration, error } = await supabase
        .from("integrations")
        .select("id, instance_identifier, provider, status, config")
        .eq("id", integrationId)
        .eq("workspace_id", workspaceId)
        .single();

      if (error || !integration) throw error ?? new Error("Integração não encontrada");

      if (integration.provider === "shopify") {
        await runShopifyDisconnect();
        return;
      }

      if (integration.provider === "netface") {
        const { error } = await supabase.functions.invoke("netface-disconnect", {
          body: { workspaceId },
        });
        if (error) throw error;
        toast({
          title: "Netface desconectado",
          description: "A sincronização foi pausada. Os produtos permanecem no catálogo.",
        });
        loadIntegrations();
        return;
      }

      if (!MESSAGING_PROVIDERS.includes(integration.provider as (typeof MESSAGING_PROVIDERS)[number])) {
        if (!confirm("Tem certeza que deseja desconectar esta integração?")) return;
        await finalizeIntegrationDisconnect(integrationId, integration, null);
        return;
      }

      const providerChannel =
        integration.provider === "instagram"
          ? "instagram"
          : integration.provider === "messenger"
            ? "messenger"
            : "whatsapp";
      const conversationCount = await fetchIntegrationConversationCount(supabase, {
        workspaceId,
        channel: providerChannel,
        integrationId,
      });
      const isHardDelete = integration.status === "disconnected";

      const { data: siblings } = await supabase
        .from("integrations")
        .select("id, provider, config, instance_identifier, status")
        .eq("workspace_id", workspaceId)
        .eq("provider", integration.provider)
        .in("status", ["active", "pending"])
        .neq("id", integrationId);

      const reassignOptions: ReassignIntegrationOption[] = (siblings || []).map((row) => ({
        id: row.id,
        label: integrationDisconnectLabel(row as any),
        detail: integrationDisconnectDetail(row as any),
      }));

      setDisconnectTarget({
        id: integration.id,
        provider: integration.provider,
        status: integration.status,
        label: integrationDisconnectLabel(integration as any),
        isHardDelete,
      });
      setDisconnectConversationCount(conversationCount);
      setDisconnectReassignOptions(reassignOptions);
      setDisconnectDialogOpen(true);
    } catch (err) {
      console.error("openDisconnectDialog:", err);
      toast({
        title: "Erro",
        description: "Não foi possível preparar a desconexão.",
        variant: "destructive",
      });
    } finally {
      setDisconnectPreparing(false);
    }
  };

  const runShopifyDisconnect = async () => {
    if (!workspaceId) return;
    const { error } = await supabase.functions.invoke("shopify-disconnect", {
      body: { workspaceId },
    });
    if (error) throw error;
    toast({
      title: "Integração removida",
      description: "A conexão Shopify foi removida definitivamente.",
    });
    loadIntegrations();
  };

  const finalizeIntegrationDisconnect = async (
    integrationId: string,
    integration: { instance_identifier?: string | null; provider: string; status: string | null },
    choice: DisconnectConversationChoice | null,
  ) => {
    if (!workspaceId) return;

    if (choice && integration.provider !== "shopify") {
      await applyDisconnectConversationChoice(workspaceId, integrationId, choice);
    }

    if (integration.provider === "whatsapp_evolution" && integration.instance_identifier) {
      try {
        const { data: deleteResult, error: deleteError } = await supabase.functions.invoke("evolution-connect", {
          body: {
            workspaceId,
            action: "delete",
            instanceIdentifier: integration.instance_identifier,
          },
        });

        if (deleteError) {
          console.error("Erro ao deletar instância:", deleteError);
          toast({
            title: "⚠️ Aviso",
            description: "Instância pode não ter sido removida da Evolution API. Verifique manualmente.",
            variant: "destructive",
          });
        } else if (deleteResult?.success === false) {
          toast({
            title: "⚠️ Aviso",
            description: deleteResult.warning || "Instância pode não ter sido removida da Evolution API.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Erro ao chamar delete da instância:", error);
        toast({
          title: "⚠️ Aviso",
          description: "Erro ao deletar instância na Evolution API. Verifique manualmente.",
          variant: "destructive",
        });
      }
    }

    if (integration.provider === "whatsapp_uazapi" && integration.instance_identifier) {
      try {
        const { data: deleteResult, error: deleteError } = await supabase.functions.invoke("uazapi-connect", {
          body: {
            workspaceId,
            action: "delete",
            integrationId,
            instanceIdentifier: integration.instance_identifier,
          },

        });

        if (deleteError) {
          console.error("Erro ao deletar instância WhatsApp Business:", deleteError);
          toast({
            title: "⚠️ Aviso",
            description: "A conexão pode não ter sido removida no servidor. Verifique manualmente.",
            variant: "destructive",
          });
        } else if (deleteResult?.success === false) {
          toast({
            title: "⚠️ Aviso",
            description: deleteResult.warning || "A conexão pode não ter sido removida no servidor.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Erro ao chamar delete da instância WhatsApp Business:", error);
        toast({
          title: "⚠️ Aviso",
          description: "Erro ao remover a conexão no servidor. Verifique manualmente.",
          variant: "destructive",
        });
      }
    }

    if (integration.provider === "whatsapp_cloud") {
      try {
        const { data: cloudResult, error: cloudError } = await supabase.functions.invoke(
          "whatsapp-cloud-disconnect",
          { body: { workspaceId, integrationId } },
        );

        if (cloudError) {
          console.error("Erro ao desconectar WhatsApp Cloud na Meta:", cloudError);
          toast({
            title: "⚠️ Aviso",
            description:
              "A integração será desativada localmente, mas a Meta pode não ter sido notificada. Verifique no Business Suite se necessário.",
            variant: "destructive",
          });
        } else if (cloudResult?.graph_skipped) {
          console.warn("[whatsapp-cloud-disconnect] Graph skipped — sem token");
        } else {
          const deregister = cloudResult?.steps?.deregister_phone;
          const unsubscribe = cloudResult?.steps?.unsubscribe_waba;
          if (deregister && !deregister.ok && !deregister.skipped) {
            toast({
              title: "⚠️ Aviso",
              description: "Deregister na Meta pode não ter concluído. A integração foi desativada localmente.",
              variant: "destructive",
            });
          } else if (unsubscribe && !unsubscribe.ok) {
            toast({
              title: "⚠️ Aviso",
              description:
                "Unsubscribe de webhooks na Meta pode não ter concluído. A integração foi desativada localmente.",
              variant: "destructive",
            });
          }
        }
      } catch (error) {
        console.error("Erro ao chamar whatsapp-cloud-disconnect:", error);
        toast({
          title: "⚠️ Aviso",
          description: "Erro ao notificar a Meta. A integração será desativada localmente.",
          variant: "destructive",
        });
      }
    }

    const isHardDelete = integration.status === "disconnected";

    if (isHardDelete) {
      const { error } = await supabase.from("integrations").delete().eq("id", integrationId);
      if (error) throw error;
    } else {
      const disconnectUpdate: Record<string, unknown> = {
        status: "disconnected" as const,
        access_token: null,
        token_expires_at: null,
      };
      if (integration.provider === "whatsapp_cloud") {
        disconnectUpdate.credentials_encrypted = null;
      }
      const { error } = await supabase
        .from("integrations")
        .update(disconnectUpdate)
        .eq("id", integrationId);
      if (error) throw error;
    }

    const reassigned =
      choice?.mode === "reassign"
        ? " Conversas reatribuídas."
        : choice?.mode === "unassigned"
          ? " Conversas ficaram em aberto no canal."
          : "";

    toast({
      title: isHardDelete ? "Integração removida" : "Integração desconectada",
      description: isHardDelete
        ? `A integração foi removida permanentemente.${reassigned}`
        : `A conexão foi desconectada. Reconecte para usar novamente.${reassigned}`,
    });

    loadIntegrations();
  };

  const handleDisconnectConfirm = async (choice: DisconnectConversationChoice) => {
    if (!disconnectTarget || !workspaceId) return;

    const { data: integration } = await supabase
      .from("integrations")
      .select("instance_identifier, provider, status")
      .eq("id", disconnectTarget.id)
      .single();

    if (!integration) throw new Error("Integração não encontrada");

    const effectiveChoice =
      disconnectConversationCount > 0 ? choice : ({ mode: "keep" } as DisconnectConversationChoice);

    await finalizeIntegrationDisconnect(disconnectTarget.id, integration, effectiveChoice);
  };

  const handleDeleteIntegration = (integrationId: string) => {
    void openDisconnectDialog(integrationId);
  };

  const whatsappCloudAllowed = Boolean(
    workspaceId &&
      WHATSAPP_CLOUD_ALLOWED_WORKSPACES.has(workspaceId),
  );
  const uazapiAllowed = isUazapiEnabledForWorkspace(workspaceId);
  const whatsappCloudIntegrations = whatsappCloudList;
  const [waCloudConnecting, setWaCloudConnecting] = useState<"standard" | "coexistence" | null>(null);

  const accordionConnectionContext = useMemo(
    (): AccordionConnectionContext => ({
      workspaceId,
      isOwner,
      uazapiAllowed,
      whatsappIntegrations,
      whatsappUazapiIntegrations,
      whatsappCloudIntegrations: whatsappCloudList,
      activeIntegrations,
      calendarConnections,
      calendarTokenInvalid,
    }),
    [
      workspaceId,
      isOwner,
      uazapiAllowed,
      whatsappIntegrations,
      whatsappUazapiIntegrations,
      whatsappCloudList,
      activeIntegrations,
      calendarConnections,
      calendarTokenInvalid,
    ],
  );

  const visibleIntegrations = useMemo(() => {
    const list: AccordionIntegrationItem[] = whatsappCloudAllowed
      ? [WHATSAPP_CLOUD_INTEGRATION, ...integrations]
      : [...integrations];
    const visible = SHOPIFY_UI_ENABLED
      ? list
      : list.filter((item) => item.type !== "shopify");
    return sortAccordionIntegrations(visible, accordionConnectionContext);
  }, [whatsappCloudAllowed, accordionConnectionContext]);

  const hasActiveWhatsAppCloud = whatsappCloudIntegrations.some((w) => w.status === "active");
  const hasPendingWhatsAppCloud = whatsappCloudIntegrations.some((w) => w.status === "pending");
  const hasDisconnectedWhatsAppCloud = whatsappCloudIntegrations.some(
    (w) => w.status === "disconnected" || w.status === "error",
  );
  const isWhatsAppCloudPartialDown = hasActiveWhatsAppCloud && hasDisconnectedWhatsAppCloud;

  const handleConnectWhatsAppCloud = async (flow: "standard" | "coexistence" = "standard") => {
    if (!workspaceId) return;
    if (!whatsappCloudAllowed) {
      toast({
        title: "Integração indisponível",
        description: "WhatsApp Cloud ainda não está liberado para este workspace.",
        variant: "destructive",
      });
      return;
    }
    if (!(await ensureCanConnectMessagingChannel())) return;
    setWaCloudConnecting(flow);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-cloud-oauth", {
        body: { workspace_id: workspaceId, flow },
      });
      if (error) throw error;
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      } else {
        throw new Error(data?.error || "Falha ao gerar URL");
      }
    } catch (err: any) {
      toast({
        title: "Erro ao conectar WhatsApp Cloud",
        description: err?.message || "Tente novamente em instantes.",
        variant: "destructive",
      });
      setWaCloudConnecting(null);
    }
  };

  const handleVerifyWhatsAppCloud = (integration: any) => {
    const phoneNumberId = integration?.config?.phone_number_id || integration?.instance_identifier;
    if (!phoneNumberId) return;
    const params = new URLSearchParams({
      success: "0",
      needs_verification: "1",
      phone_number_id: phoneNumberId,
      phone: integration?.config?.display_phone_number || integration?.instance_identifier || phoneNumberId,
    });
    window.location.href = `/oauth/whatsapp-cloud?${params.toString()}`;
  };

  return (
    <div className="p-2 sm:p-4 lg:p-6 max-w-5xl mx-auto space-y-4 sm:space-y-5 lg:space-y-6 relative">
      {shouldShowPaywall && <PaywallOverlay featureName="Integrações" minPlan="Inicial" />}
      
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Integrações</h1>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-xs sm:text-sm lg:text-base">
          Conecte seus canais de comunicação e ferramentas
        </p>
      </div>

      <Card>
        <CardContent className="p-3 sm:p-4 lg:p-6">
          <Accordion type="multiple" className="w-full">
            {visibleIntegrations.map((integration) => {
              // Shopify: em breve apenas para não-owners. Messenger: em breve, exceto para workspaces liberados.
              const messengerAllowed = MESSENGER_ALLOWED_WORKSPACES.has(workspaceId ?? "");
              const isComingSoon =
                (!isOwner && (integration.type === "shopify" || integration.type === "netface")) ||
                (integration.type === "messenger" && !messengerAllowed);
              
              const effectiveStatus = isComingSoon ? "coming_soon" : integration.status;

              // WhatsApp Business — accordion único (Evolution legado ou Uazapi conforme flag)
              const evolutionWhatsAppConnections = whatsappIntegrations;
              const uazapiWhatsAppConnections = whatsappUazapiIntegrations;
              const displayedWhatsAppConnections = uazapiAllowed
                ? (uazapiWhatsAppConnections.length > 0
                    ? uazapiWhatsAppConnections
                    : evolutionWhatsAppConnections)
                : integration.type === "evolution_api"
                  ? evolutionWhatsAppConnections
                  : [];
              const hasActiveDisplayedWhatsApp = displayedWhatsAppConnections.some(
                (w) => w.status === "active" && !needsWhatsappReconnect(w),
              );
              const hasPendingDisplayedWhatsApp = displayedWhatsAppConnections.some(
                (w) => w.status === "pending" && !needsWhatsappReconnect(w),
              );
              const hasDisconnectedDisplayedWhatsApp = displayedWhatsAppConnections.some((w) =>
                needsWhatsappReconnect(w),
              );
              const isWhatsAppBusinessAccordion = integration.type === "evolution_api";
              const whatsAppConnectionsForDisplay = isWhatsAppBusinessAccordion
                ? displayedWhatsAppConnections.map((w) => ({
                    data: w,
                    onReconnect: handleReconnectWhatsApp,
                  }))
                : [];
              const openNewWhatsAppConnection = uazapiAllowed
                ? openWhatsAppBusinessConnect
                : openWhatsAppLegacyConnect;

              const integrationData =
                integration.type === "google_calendar" ? activeIntegrations.google_calendar :
                  integration.type === "instagram" ? activeIntegrations.instagram :
                    integration.type === "messenger" ? activeIntegrations.messenger :
                      integration.type === "shopify" ? activeIntegrations.shopify :
                        integration.type === "netface" ? activeIntegrations.netface :
                        null;

              // Instagram/Messenger status checks (mirror WhatsApp pattern) — also treat `error` as disconnected
              const isInstagramDisconnected = integration.type === "instagram" && integrationData && (integrationData.status === 'disconnected' || integrationData.status === 'error');
              const isInstagramTokenExpired = integration.type === "instagram" && integrationData?.status === 'active' && integrationData?.token_expires_at && new Date(integrationData.token_expires_at) < new Date();
              const isMessengerDisconnected = integration.type === "messenger" && integrationData && (integrationData.status === 'disconnected' || integrationData.status === 'error');
              const isMessengerTokenExpired = integration.type === "messenger" && integrationData?.status === 'active' && integrationData?.token_expires_at && new Date(integrationData.token_expires_at) < new Date();

              const isConnected = isAccordionIntegrationConnected(
                integration,
                accordionConnectionContext,
              );

              // Verificar se o token do Google Calendar está inválido
              const isCalendarTokenInvalid = 
                integration.type === "google_calendar" && 
                activeIntegrations.google_calendar && 
                calendarTokenInvalid;
              
              const isInstagramNeedsReconnect = isInstagramDisconnected || isInstagramTokenExpired;
              const isMessengerNeedsReconnect = isMessengerDisconnected || isMessengerTokenExpired;

              const isEvolutionPartialDown =
                integration.type === "evolution_api" &&
                hasActiveDisplayedWhatsApp &&
                hasDisconnectedDisplayedWhatsApp;



              const isWhatsAppCloudRow = integration.type === "whatsapp_cloud";

              return (
                <AccordionItem key={integration.type} value={integration.type} className="border-b last:border-0">
                  <AccordionTrigger
                    className="hover:no-underline py-3 sm:py-4 lg:py-6 px-2 sm:px-4"
                  >
                    <div className="flex w-full items-center justify-between pr-2 sm:pr-4">
                      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3 lg:gap-4">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 ${isConnected ? 'bg-primary/10' : 'bg-muted'
                          }`}>
                          <integration.Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isConnected ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="min-w-0 flex-1 text-left">
                          <div
                            className={
                              isWhatsAppCloudRow
                                ? "flex min-w-0 items-center gap-1.5 sm:gap-2"
                                : "flex items-center gap-1.5 sm:gap-2"
                            }
                          >
                            <h3 className="min-w-0 truncate font-semibold text-sm sm:text-base">{integration.name}</h3>
                            {isWhatsAppCloudRow && (
                              <Badge variant="outline" className="hidden shrink-0 text-[10px] sm:inline-flex">
                                Meta
                              </Badge>
                            )}
                            {isConnected && !isEvolutionPartialDown && !isWhatsAppCloudPartialDown && (
                              <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary flex-shrink-0" />
                            )}
                          </div>
                          <p
                            className={
                              isWhatsAppCloudRow
                                ? "mt-0.5 line-clamp-2 text-xs text-muted-foreground sm:mt-1 sm:line-clamp-1 sm:text-sm"
                                : "text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 line-clamp-1"
                            }
                          >
                            {integration.description}
                          </p>
                        </div>
                      </div>
                      <div className="ml-2 flex shrink-0 items-center">
                        {effectiveStatus === "coming_soon" ? (
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">Em breve</Badge>
                        ) : (integration.type === "evolution_api" && isEvolutionPartialDown) ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">Reconectar conexão</Badge>
                        ) : (integration.type === "whatsapp_cloud" && isWhatsAppCloudPartialDown) ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">Reconectar conexão</Badge>
                        ) : (integration.type === "evolution_api" && hasPendingDisplayedWhatsApp && !hasActiveDisplayedWhatsApp) ? (
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">Conectando...</Badge>
                        ) : (integration.type === "whatsapp_cloud" && hasPendingWhatsAppCloud && !hasActiveWhatsAppCloud) ? (
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">Conectando...</Badge>
                        ) : (integration.type === "evolution_api" && !hasActiveDisplayedWhatsApp && hasDisconnectedDisplayedWhatsApp) ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">Desconectado</Badge>
                        ) : (integration.type === "whatsapp_cloud" && !hasActiveWhatsAppCloud && hasDisconnectedWhatsAppCloud) ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">Desconectado</Badge>
                        ) : isInstagramNeedsReconnect ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">{isInstagramTokenExpired ? "Token Expirado" : "Desconectado"}</Badge>
                        ) : isMessengerNeedsReconnect ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">{isMessengerTokenExpired ? "Token Expirado" : "Desconectado"}</Badge>
                        ) : isCalendarTokenInvalid ? (
                          <Badge variant="destructive" className="text-[10px] sm:text-xs">Reconectar</Badge>
                        ) : isConnected ? (
                          <Badge className="bg-primary/10 text-primary hover:bg-primary/20 text-[10px] sm:text-xs">Conectado</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] sm:text-xs">Disponível</Badge>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>


                  <AccordionContent className="pb-4 sm:pb-5 lg:pb-6 pt-2 px-2 sm:px-4">
                    <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-12 lg:pl-16">
                      {/* WhatsApp Details — renders ALL connections */}
                      {isWhatsAppBusinessAccordion && (
                        <>
                          {whatsAppConnectionsForDisplay.length > 0 ? (
                            <div className="space-y-2">
                              {whatsAppConnectionsForDisplay.map(({ data: waData, onReconnect }) => {
                                const hasPhone = !!waData.config?.phone_number;
                                const hasQrPending = !!waData.config?.qrcode?.base64;
                                const isAwaitingScan = waData.status === "pending" && hasQrPending && !hasPhone;
                                const isConnectingState = getConnectionState(waData) === "connecting";
                                const isActive = waData.status === "active" && !isAwaitingScan && !isConnectingState;
                                const isDisconnected =
                                  waData.status === "disconnected" || waData.status === "error" || isConnectingState;
                                const isPending = waData.status === "pending" && !isAwaitingScan;
                                const connectionLabel = whatsappIntegrationDisplayLabel(waData, "Sem nome");
                                const phoneDisplay = waData.config?.phone_number
                                  ? formatPhoneNumber(waData.config.phone_number)
                                  : isAwaitingScan
                                    ? "Aguardando escaneamento do QR"
                                    : isPending || isConnectingState
                                      ? "Conectando..."
                                      : "Aguardando sincronização...";

                                const statusBadge = isActive ? (
                                  <Badge variant="default" className="text-xs shrink-0">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Conectado
                                  </Badge>
                                ) : isDisconnected ? (
                                  <Badge variant="destructive" className="text-xs shrink-0">
                                    Desconectado
                                  </Badge>
                                ) : isAwaitingScan ? (
                                  <Badge variant="outline" className="text-xs shrink-0">
                                    Aguardando QR
                                  </Badge>
                                ) : isPending ? (
                                  <Badge variant="secondary" className="text-xs shrink-0">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Conectando...
                                  </Badge>
                                ) : null;

                                return (
                                  <Collapsible key={waData.id} defaultOpen={false} className="rounded-lg border">
                                    <CollapsibleTrigger className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate">{connectionLabel}</p>
                                        <p className="text-xs text-muted-foreground truncate mt-0.5">{phoneDisplay}</p>
                                      </div>
                                      {statusBadge}
                                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                    </CollapsibleTrigger>

                                    <CollapsibleContent>
                                      <div className="space-y-3 border-t px-3 pb-3 pt-3">
                                        {isActive && waData.config ? (
                                          <>
                                            {waData.config?.display_name && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Perfil:</span>{" "}
                                                {waData.config.display_name}
                                              </p>
                                            )}

                                            <div className="rounded-md bg-muted/30 p-3 space-y-3">
                                              <div className="grid gap-3 sm:grid-cols-2">
                                                <div className="space-y-1.5">
                                                  <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                    <MessengerIcon className="h-3.5 w-3.5" />
                                                    Agente
                                                  </Label>
                                                  <Select
                                                    key={`${waData.id}-wa-assigned-agent`}
                                                    value={waData.assigned_agent_id || "none"}
                                                    onValueChange={(value) =>
                                                      handleUpdateIntegrationAgent(
                                                        waData.id,
                                                        "assigned_agent_id",
                                                        value === "none" ? null : value
                                                      )
                                                    }
                                                    disabled={updatingAgent === `${waData.id}-assigned_agent_id`}
                                                  >
                                                    <SelectTrigger className="h-9 w-full text-sm">
                                                      <SelectValue placeholder="Selecione um agente" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <IntegrationAgentSelectItems agents={agents} />
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                                <div className="space-y-1.5">
                                                  <Label className="text-xs text-muted-foreground">Funil de entrada</Label>
                                                  <Select
                                                    key={`${waData.id}-wa-funnel`}
                                                    value={waData.config?.selected_funnel_id || "auto"}
                                                    onValueChange={(value) =>
                                                      handleUpdateIntegrationFunnel(
                                                        waData.id,
                                                        value === "auto" ? null : value
                                                      )
                                                    }
                                                    disabled={updatingFunnelForIntegrationId === waData.id}
                                                  >
                                                    <SelectTrigger className="h-9 w-full text-sm">
                                                      <SelectValue placeholder="Selecione o funil" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="auto">Automático</SelectItem>
                                                      {funnels.map((funnel) => (
                                                        <SelectItem key={funnel.id} value={funnel.id}>
                                                          {resolveFunnelSelectLabel(funnel, integrationById)}
                                                        </SelectItem>
                                                      ))}
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                                <IntegrationInboundLeadFields
                                                  integrationId={waData.id}
                                                  workspaceId={workspaceId!}
                                                  config={(waData.config as Record<string, unknown>) ?? {}}
                                                  selectedFunnelId={
                                                    (waData.config?.selected_funnel_id as string | undefined) ??
                                                    null
                                                  }
                                                  disabled={updatingFunnelForIntegrationId === waData.id}
                                                  onConfigUpdated={(next) =>
                                                    applyIntegrationConfigLocally(waData.id, next)
                                                  }
                                                />
                                              </div>
                                              <p className="text-xs text-muted-foreground">{INTEGRATION_AGENT_HINT}</p>
                                            </div>

                                            {SHOW_WHATSAPP_HISTORY_SYNC && (
                                              <div className="space-y-1.5">
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => handleSyncWhatsappHistory(waData.id)}
                                                  disabled={
                                                    syncingIntegrationId === waData.id ||
                                                    (waData.config as any)?.sync_status === "running"
                                                  }
                                                  className="w-full"
                                                >
                                                  {(syncingIntegrationId === waData.id ||
                                                    (waData.config as any)?.sync_status === "running") ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                  ) : (
                                                    <RefreshCw className="w-4 h-4 mr-2" />
                                                  )}
                                                  {(waData.config as any)?.sync_status === "running"
                                                    ? "Sincronizando…"
                                                    : "Sincronizar conversas"}
                                                </Button>
                                                {(waData.config as any)?.sync_stats &&
                                                  (waData.config as any)?.sync_status !== "running" && (
                                                    <p className="text-xs text-muted-foreground text-center">
                                                      Última: {(waData.config as any).sync_stats.chats ?? 0} conversas,{" "}
                                                      {(waData.config as any).sync_stats.messages_inserted ?? 0} mensagens novas
                                                      {(waData.config as any).sync_status === "error" ? " — com erros" : ""}
                                                    </p>
                                                  )}
                                              </div>
                                            )}

                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              onClick={() => handleDeleteIntegration(waData.id)}
                                              className="w-full"
                                            >
                                              <Trash2 className="w-4 h-4 mr-2" />
                                              Desconectar
                                            </Button>
                                          </>
                                        ) : isDisconnected ? (
                                          <>
                                            {waData.config?.phone_number && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Número:</span>{" "}
                                                {formatPhoneNumber(waData.config.phone_number)}
                                              </p>
                                            )}
                                            <div className="flex gap-2">
                                              <Button
                                                variant="default"
                                                size="sm"
                                                onClick={() => onReconnect(waData)}
                                                className="flex-1"
                                              >
                                                <MessageCircle className="w-4 h-4 mr-2" />
                                                Reconectar
                                              </Button>
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteIntegration(waData.id)}
                                              >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Excluir
                                              </Button>
                                            </div>
                                          </>
                                        ) : isAwaitingScan ? (
                                          <>
                                            <p className="text-sm text-muted-foreground">
                                              Escaneie o QR Code com seu WhatsApp para finalizar a conexão.
                                            </p>
                                            <div className="flex gap-2">
                                              <Button
                                                variant="default"
                                                size="sm"
                                                onClick={() => onReconnect(waData)}
                                                className="flex-1"
                                              >
                                                <MessageCircle className="w-4 h-4 mr-2" />
                                                Mostrar QR Code
                                              </Button>
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteIntegration(waData.id)}
                                              >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Excluir
                                              </Button>
                                            </div>
                                          </>
                                        ) : isPending ? (
                                          <p className="text-sm text-muted-foreground">
                                            Aguardando conclusão da conexão...
                                          </p>
                                        ) : null}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                );
                              })}

                              {/* Botão Nova Conexão sempre visível */}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => void openNewWhatsAppConnection()}
                                className="w-full h-8"
                              >
                                <MessageCircle className="w-4 h-4 mr-2" />
                                Nova Conexão
                              </Button>
                              {WHATSAPP_BUSINESS_ACCOUNT_RECOMMENDATION}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">
                                Conecte seu WhatsApp Business para começar a automatizar conversas.
                              </p>
                              <Button
                                variant="default"
                                onClick={() => handleConfigure(integration.type)}
                                className="w-full"
                              >
                                <MessageCircle className="w-4 h-4 mr-2" />
                                Conectar WhatsApp
                              </Button>
                              {WHATSAPP_BUSINESS_ACCOUNT_RECOMMENDATION}
                            </div>
                          )}
                        </>
                      )}

                      {/* WhatsApp API Oficial (Cloud) — múltiplos números */}
                      {integration.type === "whatsapp_cloud" && (
                        <>
                          {whatsappCloudIntegrations.length > 0 ? (
                            <div className="space-y-2">
                              {whatsappCloudIntegrations.map((waData) => {
                                const isActive = waData.status === "active";
                                const isDisconnected =
                                  waData.status === "disconnected" || waData.status === "error";
                                const isPending = waData.status === "pending";
                                const needsVerification =
                                  isPending &&
                                  waData?.config?.code_verification_status &&
                                  waData.config.code_verification_status !== "VERIFIED";
                                const connectionLabel = whatsappIntegrationDisplayLabel(waData, "Sem nome");
                                const accountUrl = getWhatsAppCloudAccountUrl(waData.config);
                                const phoneRaw =
                                  waData.config?.display_phone_number || waData.instance_identifier;
                                const phoneDisplay = phoneRaw
                                  ? formatPhoneNumber(phoneRaw)
                                  : isPending
                                    ? needsVerification
                                      ? "Aguardando verificação"
                                      : "Conectando..."
                                    : "Aguardando sincronização...";

                                const statusBadge = isActive ? (
                                  <Badge variant="default" className="text-xs shrink-0">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Conectado
                                  </Badge>
                                ) : isDisconnected ? (
                                  <Badge variant="destructive" className="text-xs shrink-0">
                                    Desconectado
                                  </Badge>
                                ) : needsVerification ? (
                                  <Badge variant="outline" className="text-xs shrink-0">
                                    Aguardando verificação
                                  </Badge>
                                ) : isPending ? (
                                  <Badge variant="secondary" className="text-xs shrink-0">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Conectando...
                                  </Badge>
                                ) : null;

                                return (
                                  <Collapsible key={waData.id} defaultOpen={false} className="rounded-lg border">
                                    <CollapsibleTrigger className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium truncate">{connectionLabel}</p>
                                        <p className="text-xs text-muted-foreground truncate mt-0.5">{phoneDisplay}</p>
                                      </div>
                                      {statusBadge}
                                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                    </CollapsibleTrigger>

                                    <CollapsibleContent>
                                      <div className="space-y-3 border-t px-3 pb-3 pt-3">
                                        {isActive ? (
                                          <>
                                            {waData.config?.verified_name && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Perfil:</span>{" "}
                                                {waData.config.verified_name}
                                              </p>
                                            )}
                                            {waData.config?.waba_id && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Conta WABA:</span>{" "}
                                                {waData.config.waba_id}
                                              </p>
                                            )}

                                            <WhatsAppCloudWabaStatusCard config={waData.config} />

                                            {accountUrl && (
                                              <Button variant="outline" size="sm" className="w-full" asChild>
                                                <a href={accountUrl} target="_blank" rel="noreferrer">
                                                  <ExternalLink className="w-4 h-4 mr-2" />
                                                  Abrir conta na Meta
                                                </a>
                                              </Button>
                                            )}

                                            <Button variant="outline" size="sm" className="w-full" asChild>
                                              <Link
                                                to={`/integrations/whatsapp-cloud/templates?integration=${waData.id}`}
                                              >
                                                <FileText className="w-4 h-4 mr-2" />
                                                Gerenciar templates
                                              </Link>
                                            </Button>

                                            <div className="rounded-md bg-muted/30 p-3 space-y-3">
                                              <div className="grid gap-3 sm:grid-cols-2">
                                                <div className="space-y-1.5">
                                                  <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                                                    <MessengerIcon className="h-3.5 w-3.5" />
                                                    Agente
                                                  </Label>
                                                  <Select
                                                    key={`${waData.id}-cloud-assigned-agent`}
                                                    value={waData.assigned_agent_id || "none"}
                                                    onValueChange={(value) =>
                                                      handleUpdateIntegrationAgent(
                                                        waData.id,
                                                        "assigned_agent_id",
                                                        value === "none" ? null : value
                                                      )
                                                    }
                                                    disabled={updatingAgent === `${waData.id}-assigned_agent_id`}
                                                  >
                                                    <SelectTrigger className="h-9 w-full text-sm">
                                                      <SelectValue placeholder="Selecione um agente" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <IntegrationAgentSelectItems agents={agents} />
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                                <div className="space-y-1.5">
                                                  <Label className="text-xs text-muted-foreground">Funil de entrada</Label>
                                                  <Select
                                                    key={`${waData.id}-cloud-funnel`}
                                                    value={waData.config?.selected_funnel_id || "auto"}
                                                    onValueChange={(value) =>
                                                      handleUpdateIntegrationFunnel(
                                                        waData.id,
                                                        value === "auto" ? null : value
                                                      )
                                                    }
                                                    disabled={updatingFunnelForIntegrationId === waData.id}
                                                  >
                                                    <SelectTrigger className="h-9 w-full text-sm">
                                                      <SelectValue placeholder="Selecione o funil" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="auto">Automático</SelectItem>
                                                      {funnels.map((funnel) => (
                                                        <SelectItem key={funnel.id} value={funnel.id}>
                                                          {resolveFunnelSelectLabel(funnel, integrationById)}
                                                        </SelectItem>
                                                      ))}
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                                <IntegrationInboundLeadFields
                                                  integrationId={waData.id}
                                                  workspaceId={workspaceId!}
                                                  config={(waData.config as Record<string, unknown>) ?? {}}
                                                  selectedFunnelId={
                                                    (waData.config?.selected_funnel_id as string | undefined) ??
                                                    null
                                                  }
                                                  disabled={updatingFunnelForIntegrationId === waData.id}
                                                  onConfigUpdated={(next) =>
                                                    applyIntegrationConfigLocally(waData.id, next)
                                                  }
                                                />
                                              </div>
                                              <p className="text-xs text-muted-foreground">{INTEGRATION_AGENT_HINT}</p>
                                            </div>

                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              onClick={() => handleDeleteIntegration(waData.id)}
                                              className="w-full"
                                            >
                                              <Trash2 className="w-4 h-4 mr-2" />
                                              Desconectar
                                            </Button>
                                          </>
                                        ) : isDisconnected ? (
                                          <>
                                            {phoneRaw && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Número:</span>{" "}
                                                {formatPhoneNumber(phoneRaw)}
                                              </p>
                                            )}
                                            {waData.config?.verified_name && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Perfil:</span>{" "}
                                                {waData.config.verified_name}
                                              </p>
                                            )}
                                            <div className="flex gap-2">
                                              <Button
                                                variant="default"
                                                size="sm"
                                                onClick={() => handleConnectWhatsAppCloud("standard")}
                                                disabled={waCloudConnecting !== null}
                                                className="flex-1"
                                              >
                                                <MessageCircle className="w-4 h-4 mr-2" />
                                                Reconectar
                                              </Button>
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteIntegration(waData.id)}
                                              >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Excluir
                                              </Button>
                                            </div>
                                          </>
                                        ) : isPending ? (
                                          <>
                                            {waData.config?.verified_name && (
                                              <p className="text-sm text-muted-foreground">
                                                <span className="font-medium text-foreground/80">Perfil:</span>{" "}
                                                {waData.config.verified_name}
                                              </p>
                                            )}
                                            {needsVerification && (
                                              <p className="text-sm text-muted-foreground">
                                                A Meta exige verificação do número antes de ativar o envio de mensagens.
                                              </p>
                                            )}
                                            <div className="flex gap-2">
                                              {needsVerification && (
                                                <Button
                                                  variant="default"
                                                  size="sm"
                                                  onClick={() => handleVerifyWhatsAppCloud(waData)}
                                                  className="flex-1"
                                                >
                                                  Verificar número
                                                </Button>
                                              )}
                                              <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDeleteIntegration(waData.id)}
                                                className={needsVerification ? "" : "w-full"}
                                              >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Excluir
                                              </Button>
                                            </div>
                                          </>
                                        ) : null}
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                );
                              })}

                              <div className="space-y-2 pt-1">
                                <p className="text-sm text-muted-foreground">
                                  Adicione outro número à API oficial da Meta.
                                </p>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleConnectWhatsAppCloud("standard")}
                                    disabled={waCloudConnecting !== null}
                                    className="h-8 w-full"
                                  >
                                    {waCloudConnecting === "standard" ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Abrindo Meta…
                                      </>
                                    ) : (
                                      <>
                                        <MessageCircle className="w-4 h-4 mr-2" />
                                        Nova Conexão
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleConnectWhatsAppCloud("coexistence")}
                                    disabled={waCloudConnecting !== null}
                                    className="h-8 w-full"
                                  >
                                    {waCloudConnecting === "coexistence" ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Abrindo Meta…
                                      </>
                                    ) : (
                                      "WhatsApp + App Business (coexistência)"
                                    )}
                                  </Button>
                                </div>
                                {WHATSAPP_CLOUD_COEXISTENCE_HINT}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">
                                Conecte um número via API oficial da Meta para conversas com conformidade total às políticas do WhatsApp Business Platform.
                              </p>
                              <Button
                                variant="default"
                                onClick={() => handleConnectWhatsAppCloud("standard")}
                                disabled={waCloudConnecting !== null}
                                className="w-full"
                              >
                                {waCloudConnecting === "standard" ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Abrindo Meta…
                                  </>
                                ) : (
                                  <>
                                    <MessageCircle className="w-4 h-4 mr-2" />
                                    Conectar WhatsApp API Oficial
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConnectWhatsAppCloud("coexistence")}
                                disabled={waCloudConnecting !== null}
                                className="w-full h-8"
                              >
                                {waCloudConnecting === "coexistence" ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Abrindo Meta…
                                  </>
                                ) : (
                                  "WhatsApp + App Business (coexistência)"
                                )}
                              </Button>
                              {WHATSAPP_CLOUD_COEXISTENCE_HINT}
                            </div>
                          )}
                        </>
                      )}

                      {/* Google Calendar Details */}
                      {integration.type === "google_calendar" && (
                        <>
                          {isGcalMultiCalendarEnabled() ? (
                            calendarConnections.length > 0 ? (
                              <div className="space-y-4">
                                {calendarConnections.map((conn) => {
                                  const enabledAgendas = conn.syncCalendars.filter((sc) => sc.is_enabled);
                                  const connNeedsReconnect = conn.needs_reconnect;

                                  return (
                                    <div key={conn.id} className="space-y-3 border rounded-lg p-3">
                                      {connNeedsReconnect ? (
                                        <>
                                          <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                            <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                                              ⚠️ Token expirado — {conn.email}
                                            </p>
                                            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                              Reconecte para restaurar o acesso às agendas.
                                            </p>
                                          </div>
                                          <Button
                                            variant="default"
                                            onClick={handleReconnectCalendar}
                                            disabled={calendarReconnecting}
                                            className="w-full"
                                          >
                                            {calendarReconnecting ? (
                                              <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Reconectando...
                                              </>
                                            ) : (
                                              <>
                                                <Calendar className="w-4 h-4 mr-2" />
                                                Reconectar
                                              </>
                                            )}
                                          </Button>
                                        </>
                                      ) : (
                                        <>
                                          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                                            <div className="flex items-center justify-between mb-1">
                                              <p className="text-sm font-medium">{conn.email}</p>
                                              <Badge variant="default" className="text-xs">
                                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                                Conectado
                                              </Badge>
                                            </div>
                                            {enabledAgendas.length > 0 ? (
                                              <div className="space-y-1">
                                                <p className="text-xs font-medium text-muted-foreground">
                                                  Agendas sincronizadas ({enabledAgendas.length})
                                                </p>
                                                {enabledAgendas.map((sc) => (
                                                  <p key={sc.id} className="text-sm">
                                                    📅 {sc.display_name || sc.calendar_id}
                                                    {sc.is_primary && (
                                                      <span className="ml-1 text-xs text-muted-foreground">(Principal)</span>
                                                    )}
                                                  </p>
                                                ))}
                                              </div>
                                            ) : (
                                              <p className="text-sm text-muted-foreground">
                                                Nenhuma agenda habilitada ainda.
                                              </p>
                                            )}
                                          </div>

                                          <div className="grid grid-cols-2 gap-2">
                                            <Button
                                              onClick={() => openCalendarManageDialog(conn.id)}
                                              variant="outline"
                                              size="sm"
                                            >
                                              <Settings className="w-4 h-4 mr-2" />
                                              Gerenciar Agendas
                                            </Button>
                                            <Button
                                              onClick={() => setCalendarConfigOpen(true)}
                                              variant="outline"
                                              size="sm"
                                            >
                                              <Settings className="w-4 h-4 mr-2" />
                                              Configurar Eventos
                                            </Button>
                                          </div>

                                          <Button
                                            variant="destructive"
                                            onClick={() => handleDisconnectCalendar(conn.id)}
                                            className="w-full"
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Desconectar
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}

                                <Button
                                  variant="outline"
                                  onClick={handleAddCalendarAgenda}
                                  className="w-full"
                                >
                                  <Calendar className="w-4 h-4 mr-2" />
                                  Adicionar Agenda
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                  Conecte seu Google Calendar para agendamentos automáticos de reuniões.
                                </p>
                                <Button
                                  variant="default"
                                  onClick={() => handleConfigure(integration.type)}
                                  className="w-full"
                                >
                                  <Calendar className="w-4 h-4 mr-2" />
                                  Conectar Google Calendar
                                </Button>
                              </div>
                            )
                          ) : isConnected && integrationData ? (
                            <div className="space-y-3">
                              {isCalendarTokenInvalid ? (
                                // Estado de token inválido - mostrar botão de reconectar diretamente
                                <div className="space-y-3">
                                  <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                    <div className="flex items-center gap-2 mb-2">
                                      <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                                        ⚠️ Token expirado ou inválido
                                      </p>
                                    </div>
                                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                      A conexão precisa ser renovada. Clique em "Reconectar" abaixo.
                                    </p>
                                  </div>

                                  <Button
                                    variant="default"
                                    onClick={handleReconnectCalendar}
                                    disabled={calendarReconnecting}
                                    className="w-full"
                                  >
                                    {calendarReconnecting ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Reconectando...
                                      </>
                                    ) : (
                                      <>
                                        <Calendar className="w-4 h-4 mr-2" />
                                        Reconectar Google Calendar
                                      </>
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                // Estado normal conectado
                                <>
                                  <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground mb-1">Email Conectado</p>
                                      <p className="text-sm">{integrationData.email}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs font-medium text-muted-foreground mb-1">Agenda Selecionada</p>
                                      <p className="text-sm">
                                        📅 {calendarName || 'Carregando...'}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-2">
                                    <Button
                                      onClick={() => openCalendarManageDialog()}
                                      variant="outline"
                                      size="sm"
                                    >
                                      <Settings className="w-4 h-4 mr-2" />
                                      Trocar Agenda
                                    </Button>
                                    <Button
                                      onClick={() => setCalendarConfigOpen(true)}
                                      variant="outline"
                                      size="sm"
                                    >
                                      <Settings className="w-4 h-4 mr-2" />
                                      Configurar Eventos
                                    </Button>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">
                                Conecte seu Google Calendar para agendamentos automáticos de reuniões.
                              </p>
                              <Button
                                variant="default"
                                onClick={() => handleConfigure(integration.type)}
                                className="w-full"
                              >
                                <Calendar className="w-4 h-4 mr-2" />
                                Conectar Google Calendar
                              </Button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Instagram Details */}
                      {integration.type === "instagram" && (
                        <>
                          {isInstagramNeedsReconnect && integrationData ? (
                            <div className="space-y-2">
                              <Collapsible defaultOpen={false} className="rounded-lg border">
                                <CollapsibleTrigger className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">
                                      @{integrationData.config?.username || "instagram"}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                      {isInstagramTokenExpired
                                        ? "Token de acesso expirado"
                                        : "Conexão perdida — reconecte para continuar"}
                                    </p>
                                  </div>
                                  <Badge variant="destructive" className="text-xs shrink-0">
                                    {isInstagramTokenExpired ? "Token Expirado" : "Desconectado"}
                                  </Badge>
                                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="space-y-3 border-t px-3 pb-3 pt-3">
                                    <div className="flex gap-2">
                                      <Button
                                        variant="default"
                                        size="sm"
                                        onClick={() => handleConfigure(integration.type)}
                                        className="flex-1"
                                        disabled={instagramConnecting}
                                      >
                                        <Instagram className="w-4 h-4 mr-2" />
                                        {instagramConnecting ? "Reconectando..." : "Reconectar Instagram"}
                                      </Button>
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleDeleteIntegration(integrationData.id)}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Excluir
                                      </Button>
                                    </div>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          ) : isConnected && integrationData?.config ? (
                            <div className="space-y-2">
                              <Collapsible defaultOpen={false} className="rounded-lg border">
                                <CollapsibleTrigger className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted/40 transition-colors [&[data-state=open]>svg:last-child]:rotate-180">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate">
                                      @{integrationData.config?.username || "Carregando..."}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                      {integrationData.config?.page_name || "Carregando..."}
                                    </p>
                                  </div>
                                  <Badge variant="default" className="text-xs shrink-0">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Conectado
                                  </Badge>
                                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                                </CollapsibleTrigger>

                                <CollapsibleContent>
                                  <div className="space-y-3 border-t px-3 pb-3 pt-3">
                                    {instagramMediaCount === 0 && (
                                      <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30">
                                        <Instagram className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                        <p className="text-xs text-amber-700 dark:text-amber-400">
                                          Conta conectada — publique um post no Instagram para receber comentários aqui.
                                        </p>
                                      </div>
                                    )}

                                    <div className="rounded-md bg-muted/30 p-3 space-y-3">
                                      <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="space-y-1.5">
                                          <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                                            <MessengerIcon className="h-3.5 w-3.5" />
                                            Agente DMs
                                          </Label>
                                          <Select
                                            key={`${integrationData.id}-ig-assigned-agent`}
                                            value={integrationData.assigned_agent_id || "none"}
                                            onValueChange={(value) =>
                                              handleUpdateIntegrationAgent(
                                                integrationData.id,
                                                "assigned_agent_id",
                                                value === "none" ? null : value
                                              )
                                            }
                                            disabled={updatingAgent === `${integrationData.id}-assigned_agent_id`}
                                          >
                                            <SelectTrigger className="h-9 w-full text-sm">
                                              <SelectValue placeholder="Selecione um agente" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <IntegrationAgentSelectItems agents={agents} />
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div className="space-y-1.5">
                                          <Label className="text-xs text-muted-foreground">Agente Comentários</Label>
                                          <Select
                                            key={`${integrationData.id}-ig-comment-agent`}
                                            value={integrationData.comment_agent_id || "none"}
                                            onValueChange={(value) =>
                                              handleUpdateIntegrationAgent(
                                                integrationData.id,
                                                "comment_agent_id",
                                                value === "none" ? null : value
                                              )
                                            }
                                            disabled={updatingAgent === `${integrationData.id}-comment_agent_id`}
                                          >
                                            <SelectTrigger className="h-9 w-full text-sm">
                                              <SelectValue placeholder="Selecione um agente" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <IntegrationAgentSelectItems agents={agents} />
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      </div>
                                      <p className="text-xs text-muted-foreground">{INTEGRATION_AGENT_HINT}</p>

                                      <div className="space-y-3 pt-2 border-t border-border/50">
                                        <div className="flex items-center justify-between gap-3">
                                          <div className="space-y-0.5">
                                            <Label className="text-xs text-muted-foreground">
                                              Responder comentários via DM (Private Reply)
                                            </Label>
                                            <p className="text-xs text-muted-foreground">
                                              Máx. 1 DM por comentário, janela de 7 dias (Meta).
                                            </p>
                                          </div>
                                          <Switch
                                            checked={integrationData.config?.comment_dm_enabled === true}
                                            disabled={updatingCommentDmForIntegrationId === integrationData.id}
                                            onCheckedChange={(checked) =>
                                              handleUpdateIntegrationCommentDm(integrationData.id, {
                                                comment_dm_enabled: checked,
                                                comment_dm_mode: checked
                                                  ? (integrationData.config?.comment_dm_mode as string) || "ai_decides"
                                                  : "public_only",
                                              })
                                            }
                                          />
                                        </div>
                                        {integrationData.config?.comment_dm_enabled && (
                                          <div className="space-y-1.5">
                                            <Label className="text-xs text-muted-foreground">Modo de resposta</Label>
                                            <Select
                                              value={(integrationData.config?.comment_dm_mode as string) || "ai_decides"}
                                              onValueChange={(value) =>
                                                handleUpdateIntegrationCommentDm(integrationData.id, {
                                                  comment_dm_mode: value,
                                                })
                                              }
                                              disabled={updatingCommentDmForIntegrationId === integrationData.id}
                                            >
                                              <SelectTrigger className="h-9 w-full text-sm">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="public_only">Só público</SelectItem>
                                                <SelectItem value="ai_decides">IA decide</SelectItem>
                                                <SelectItem value="always_dm">Sempre DM</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <div className="rounded-md bg-muted/30 p-3 space-y-3">
                                      <Label className="text-xs text-muted-foreground">Funil de entrada</Label>
                                      <Select
                                        key={`${integrationData.id}-ig-funnel`}
                                        value={integrationData.config?.selected_funnel_id || "auto"}
                                        onValueChange={(value) =>
                                          handleUpdateIntegrationFunnel(
                                            integrationData.id,
                                            value === "auto" ? null : value
                                          )
                                        }
                                        disabled={updatingFunnelForIntegrationId === integrationData.id}
                                      >
                                        <SelectTrigger className="h-9 w-full text-sm">
                                          <SelectValue placeholder="Selecione o funil" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="auto">Automático</SelectItem>
                                          {funnels.map((funnel) => (
                                            <SelectItem key={funnel.id} value={funnel.id}>
                                              {resolveFunnelSelectLabel(funnel, integrationById)}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <div className="grid gap-3 sm:grid-cols-2">
                                        <IntegrationInboundLeadFields
                                          integrationId={integrationData.id}
                                          workspaceId={workspaceId!}
                                          config={(integrationData.config as Record<string, unknown>) ?? {}}
                                          selectedFunnelId={
                                            (integrationData.config?.selected_funnel_id as
                                              | string
                                              | undefined) ?? null
                                          }
                                          disabled={
                                            updatingFunnelForIntegrationId === integrationData.id
                                          }
                                          onConfigUpdated={(next) =>
                                            applyIntegrationConfigLocally(integrationData.id, next)
                                          }
                                        />
                                      </div>
                                    </div>

                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => handleDeleteIntegration(integrationData.id)}
                                      className="w-full"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Desconectar Instagram
                                    </Button>
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">
                                Conecte sua conta Instagram Business para automatizar DMs.
                              </p>
                              <Button
                                variant="default"
                                onClick={() => handleConfigure(integration.type)}
                                className="w-full"
                                disabled={instagramConnecting}
                              >
                                <Instagram className="w-4 h-4 mr-2" />
                                {instagramConnecting ? "Conectando..." : "Conectar Instagram"}
                              </Button>
                            </div>
                          )}
                        </>
                      )}

                      {/* Messenger Details */}
                      {integration.type === "messenger" && (
                        <>
                          {isMessengerNeedsReconnect && integrationData ? (
                            <div className="space-y-3">
                              <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20 space-y-1">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-medium">Status da Conexão</p>
                                  <Badge variant="destructive" className="text-xs">
                                    {isMessengerTokenExpired ? "Token Expirado" : "Desconectado"}
                                  </Badge>
                                </div>
                                {integrationData.config?.page_name && (
                                  <p className="text-sm text-muted-foreground">
                                    <span className="font-medium">Página:</span> {integrationData.config.page_name}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-2">
                                  {isMessengerTokenExpired 
                                    ? "O token de acesso expirou. Reconecte para continuar recebendo mensagens."
                                    : "A conexão foi perdida. Reconecte para continuar usando o Messenger."}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  variant="default"
                                  onClick={() => handleConfigure(integration.type)}
                                  className="flex-1"
                                  disabled={instagramConnecting}
                                >
                                  <MessengerIcon className="w-4 h-4 mr-2" />
                                  {instagramConnecting ? "Reconectando..." : "Reconectar Messenger"}
                                </Button>
                                <Button
                                  variant="destructive"
                                  onClick={() => handleDeleteIntegration(integrationData.id)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Excluir
                                </Button>
                              </div>
                            </div>
                          ) : isConnected && integrationData ? (
                            <div className="space-y-4">
                              <div className="p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-sm font-medium">Status da Conexão</p>
                                  <Badge variant="default" className="text-xs">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Conectado
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  <span className="font-medium">Página:</span> {integrationData.config?.page_name || integrationData.page_id || "Carregando..."}
                                </p>
                              </div>

                              {/* Agent Assignment Section - Messenger */}
                              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                                <p className="text-sm font-medium flex items-center gap-2">
                                  <MessengerIcon className="h-4 w-4" />
                                  Atribuição de Agente
                                </p>
                                
                                <div className="space-y-2">
                                  <Label className="text-xs text-muted-foreground">
                                    Agente para Messenger
                                  </Label>
                                  <Select
                                    key={`${integrationData.id}-msgr-assigned-agent`}
                                    value={integrationData.assigned_agent_id || "none"}
                                    onValueChange={(value) => 
                                      handleUpdateIntegrationAgent(
                                        integrationData.id, 
                                        "assigned_agent_id", 
                                        value === "none" ? null : value
                                      )
                                    }
                                    disabled={updatingAgent === `${integrationData.id}-assigned_agent_id`}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Selecione um agente" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <IntegrationAgentSelectItems agents={agents} />
                                    </SelectContent>
                                  </Select>
                                  <p className="text-xs text-muted-foreground">{INTEGRATION_AGENT_HINT}</p>
                                </div>
                              </div>

                              <Button
                                variant="destructive"
                                onClick={() => handleDeleteIntegration(integrationData.id)}
                                className="w-full"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Desconectar Messenger
                              </Button>
                            </div>
                          ) : MESSENGER_ALLOWED_WORKSPACES.has(workspaceId ?? "") ? (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">
                                Conecte sua página do Facebook para começar a receber e responder mensagens do Messenger com IA.
                              </p>
                              <Button
                                variant="default"
                                onClick={() => handleConfigure(integration.type)}
                                className="w-full"
                                disabled={instagramConnecting}
                              >
                                <MessengerIcon className="w-4 h-4 mr-2" />
                                {instagramConnecting ? "Conectando..." : "Conectar Messenger"}
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <p className="text-sm text-muted-foreground">
                                A integração com o Facebook Messenger estará disponível em breve. Fique atento às atualizações.
                              </p>
                            </div>
                          )}
                        </>
                      )}

                      {/* Shopify Details */}
                      {integration.type === "shopify" && (
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            {isConnected ? "Gerencie e sincronize produtos da sua loja Shopify" : "Conecte sua loja Shopify para importar produtos automaticamente"}
                          </p>
                          <Button
                            variant="default"
                            onClick={() => setShopifyDialogOpen(true)}
                            className="w-full"
                          >
                            <ShoppingBag className="w-4 h-4 mr-2" />
                            {isConnected ? "Gerenciar Produtos" : "Conectar Shopify"}
                          </Button>
                        </div>
                      )}

                      {/* Netface Details */}
                      {integration.type === "netface" && (
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground">
                            {isConnected
                              ? "Gerencie a sincronização e defina quais agentes utilizam o catálogo Netface."
                              : "Cole o link da Netface, escolha o agente principal e conecte sua loja."}
                          </p>
                          <Button
                            variant="default"
                            onClick={() => setNetfaceDialogOpen(true)}
                            className="w-full"
                          >
                            <Package className="w-4 h-4 mr-2" />
                            {isConnected ? "Gerenciar Netface" : "Conectar Netface"}
                          </Button>
                        </div>
                      )}

                      {/* Coming Soon Integrations */}
                      {integration.status === "coming_soon" && (
                        <div className="p-4 bg-muted/50 rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            Esta integração estará disponível em breve. Fique atento às atualizações!
                          </p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>

      <EvolutionApiDialog
        open={evolutionDialogOpen}
        onOpenChange={handleDialogClose}
        existingIntegration={evolutionReconnectIntegration || whatsappIntegrations[0] || null}
        reconnectMode={evolutionReconnectMode}
      />

      <WhatsAppBusinessConnectDialog
        open={businessDialogOpen}
        onOpenChange={handleBusinessDialogClose}
        existingIntegration={
          businessReconnectIntegration ||
          (uazapiAllowed
            ? whatsappUazapiIntegrations[0] || whatsappIntegrations[0] || null
            : whatsappUazapiIntegrations[0] || null)
        }
        reconnectMode={businessReconnectMode}
        migrateFromIntegrationId={businessMigrateFromIntegrationId}
      />

      <GoogleCalendarDialog
        open={calendarDialogOpen}
        mode={calendarDialogMode}
        credentialId={calendarDialogCredentialId}
        onOpenChange={(open) => {
          setCalendarDialogOpen(open);
          if (!open) {
            setCalendarDialogCredentialId(undefined);
            loadIntegrations();
          }
        }}
        onSaved={loadIntegrations}
      />

      {workspaceId && (
        <>
          <CalendarEventConfigDialog
            open={calendarConfigOpen}
            onOpenChange={setCalendarConfigOpen}
            workspaceId={workspaceId}
          />
          
          {SHOPIFY_UI_ENABLED ? (
            <ShopifyDialog
              open={shopifyDialogOpen}
              onOpenChange={setShopifyDialogOpen}
              workspaceId={workspaceId}
              onConnectionChange={loadIntegrations}
            />
          ) : null}

          <NetfaceDialog
            open={netfaceDialogOpen}
            onOpenChange={setNetfaceDialogOpen}
            workspaceId={workspaceId}
            onConnectionChange={loadIntegrations}
          />
        </>
      )}

      <Dialog open={instagramFunnelDialogOpen} onOpenChange={setInstagramFunnelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conectar Instagram</DialogTitle>
            <DialogDescription>
              Escolha como os novos leads dessa conexão devem entrar no CRM.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button
                type="button"
                variant={instagramFunnelMode === "auto" ? "default" : "outline"}
                onClick={() => {
                  setInstagramFunnelMode("auto");
                  setInstagramSelectedFunnelId("");
                }}
                className="flex-1"
              >
                Criar/usar automático
              </Button>
              <Button
                type="button"
                variant={instagramFunnelMode === "existing" ? "default" : "outline"}
                onClick={() => setInstagramFunnelMode("existing")}
                className="flex-1"
              >
                Funil existente
              </Button>
            </div>

            {instagramFunnelMode === "existing" && (
              <div className="space-y-2">
                <Label>Selecione o funil</Label>
                <Select
                  value={instagramSelectedFunnelId || "none"}
                  onValueChange={(value) => setInstagramSelectedFunnelId(value === "none" ? "" : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selecione...</SelectItem>
                    {funnels.map((funnel) => (
                      <SelectItem key={funnel.id} value={funnel.id}>
                        {resolveFunnelSelectLabel(funnel, integrationById)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInstagramFunnelDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (instagramFunnelMode === "existing" && !instagramSelectedFunnelId) {
                  toast({
                    title: "Selecione um funil",
                    description: "Escolha um funil existente para continuar.",
                    variant: "destructive",
                  });
                  return;
                }
                setInstagramFunnelDialogOpen(false);
                await handleInstagramConnect();
              }}
              disabled={instagramConnecting}
            >
              {instagramConnecting ? "Conectando..." : "Continuar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={integrationUpgradeOpen} onOpenChange={setIntegrationUpgradeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center sm:text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle>Limite de conexões atingido</DialogTitle>
            <DialogDescription>
              {integrationLimitInfo ? (
                <>
                  Seu plano{" "}
                  <span className="font-medium text-foreground">
                    {PLAN_LABELS[integrationLimitInfo.plan] ?? integrationLimitInfo.plan}
                  </span>{" "}
                  permite até {integrationLimitInfo.limit} canal
                  {integrationLimitInfo.limit === 1 ? "" : "is"} de mensagem
                  {integrationLimitInfo.limit === 1 ? "" : "s"} (WhatsApp, Instagram ou Messenger).
                  Você já possui {integrationLimitInfo.current} conectado
                  {integrationLimitInfo.current === 1 ? "" : "s"}.
                </>
              ) : (
                "Faça upgrade do plano para conectar mais canais de mensagem."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button className="w-full" onClick={() => navigate("/planos")}>
              Ver planos e fazer upgrade
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setIntegrationUpgradeOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DisconnectIntegrationDialog
        open={disconnectDialogOpen}
        onOpenChange={setDisconnectDialogOpen}
        target={disconnectTarget}
        conversationCount={disconnectConversationCount}
        reassignOptions={disconnectReassignOptions}
        onConfirm={handleDisconnectConfirm}
      />
    </div>
  );
}
